// src/v2/pulse/PulsePage.jsx — canonical V2 Pulse page.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { buildLeaseUrl, buildLocationUrl, buildMarketPlanUrl } from "../../services/buildUrl.js";
import { PortfolioCommandPage } from "./PortfolioCommandPage.jsx";
import { useFeatureFlagContext, FEATURE_IN_FOCUS_PANEL, IN_FOCUS_LAYOUT, FEATURE_SCORING_MODEL_ARCHIVED } from "../../contexts/FeatureFlagContext.jsx";
import { Scale, TriangleAlert, CircleCheck, RadioTower, FolderKanban, Clock, Info, Lightbulb } from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, Cell, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from "recharts";
import { T } from "../../app/tokens.js";
import { TooltipParagraphs } from "../../components/Tooltip.jsx";
import { useHoverIntent } from "../../components/useHoverIntent.js";
import { useChartHoverIntent } from "../../components/useChartHoverIntent.js";
import { KpiTile } from "../../components/KpiTile.jsx";
import { KpiPopover as HomeKpiPopover, HOME_KPI_TOOLTIP_STYLE } from "../../components/KpiPopover.jsx";
import { formatReportingMonth, formatReportingPeriodStatus, reportingMonthKey } from "../../services/reportingPeriod.js";
import { KPI_TILE_ROW_HEIGHT } from "../../features/plan/KpiTileRow.jsx";
import { KpiBand } from "../../components/KpiBand.jsx";
import { getDisplayedSqft } from "../../utils/locationSqftDerived.js";
import { dateOnly } from "../../../shared/leaseAreaBasis.js";
import { USABLE_CAPACITY_STAGES_SET } from "../../utils/capacityStages.js";
import {
  getPulseAttendance,
  getPulseNextDecisions,
  getPulseNeedsAttention,
  getPulseLocationRisk,
  getPulseCategoryImages,
  acknowledgeInFocusCard,
  snoozeInFocusCard,
  restoreInFocusCard,
} from "../../data/pulseClient.js";
import { getCriticalDates, getPortfolioLeases } from "../../data/leasesPortfolioClient.js";
import { deriveLeaseActions } from "../../features/leases/deriveLeaseActions.js";
import { getPlan } from "../../data/marketsClient.js";
import { getLocations } from "../../data/locationsClient.js";
import { getOpenRolesReport } from "../../data/openRolesAdapter.js";
import { getHeadcountTrendReport } from "../../data/reportsClient.js";
import { computeOpenRolesInsight, computeHeadcountGrowthInsight } from "../../features/reports/insightRules.js";
import { SlideSheet } from "../../components/SlideSheet.jsx";
import { DrawerCloseButton } from "../components/DrawerCloseButton.jsx";
import { LeaseCommitmentsBand } from "./LeaseCommitmentsBand.jsx";
import { PULSE_PANE_H, OUTBOUND_LINK_STYLE, INFOCUS_SECTION_H, INFOCUS_TILE_H } from "./pulseConstants.js";
import { SpaceHeadroomV2 } from "./SpaceHeadroomV2.jsx";
import { PortfolioMotionPane } from "./PortfolioMotionPane.jsx";
import { buildPortfolioMotionModel } from "./portfolioMotion.js";
import { LinkedWorkflowRunsIndicator } from "../../features/workflows/LinkedWorkflowRunsIndicator.jsx";
import { PortfolioCommandTile } from "./PortfolioCommandTile.jsx";
import { CarouselArrowButton } from "./CarouselArrowButton.jsx";
import { subscribePlanInvalidation, unsubscribePlanInvalidation } from "../../services/cacheBus.js";
import { useSensitivityPolicy } from "../../contexts/SensitivityPolicyContext.jsx";
import { getDistinctMarketCount } from "../../features/plan/marketsService.js";
import { LocationScopePicker, OfficeLocationLabel, PickerOption, PickerPopover } from "./LocationScopePicker.jsx";
import { ToolbarViewToggle } from "../components/ToolbarViewToggle.jsx";
import { homeNavEnabled } from "./homeNav.js";
import { ReconciliationFindingAlert } from "../../features/home/ReconciliationFindingAlert.jsx";
import { getProjects, getAllNextMilestones } from "../../data/projectsClient.js";
import { formatFxBasisSummary } from "../../data/useFxConversionBasis.js";
import { getFxBasisTransparency } from "../../data/fxRatesClient.js";
import { deriveAnnualRentKpi, selectDefaultPortfolioLeases } from "../../features/leases/annualRentKpi.js";
import { fmtKpiUsd } from "../../utils/fmtAbbreviated.js";


const MONTHS = ["Jul","Aug","Sep","Oct","Nov","Dec","Jan","Feb","Mar","Apr","May","Jun"];

/* ─── Adapters ───────────────────────────────────────────────────────────────
 * Each adapter converts a live API response to the v2 display shape.
 * Returns null when the API response is absent or invalid; callers fall
 * back to PILOT_* constants in that case.
 */


/**
 * Validates the live location-risk API response and returns { cards, dismissedCards },
 * or null when the response is empty or malformed (callers receive an empty card array).
 */
export function adaptLocationRisk(result) {
  const cards         = result?.cards;
  const dismissed     = result?.dismissedCards;
  const hasCards      = Array.isArray(cards) && cards.length > 0;
  const hasDismissed  = Array.isArray(dismissed) && dismissed.length > 0;
  if (!hasCards && !hasDismissed) return null;
  return {
    cards:         hasCards    ? cards    : [],
    dismissedCards: hasDismissed ? dismissed : [],
  };
}

/* ─── Colour helpers ─────────────────────────────────────────────────────── */

const TONE_BG = {
  red:   "var(--v2-red-tint)",
  amber: "var(--v2-amber-tint)",
  green: "var(--v2-green-tint)",
  blue:  "var(--v2-blue-tint)",
  slate: "var(--v2-tint)",
};
const TONE_BORDER = {
  red:   "var(--v2-red-border)",
  amber: "var(--v2-amber-border)",
  green: "var(--v2-green-border)",
  blue:  "var(--v2-blue-border)",
  slate: "var(--v2-line)",
};
const TONE_FG = {
  red:   "var(--v2-tile-red-fg)",
  amber: "var(--v2-tile-amber-fg)",
  green: "var(--v2-tile-green-fg)",
  blue:  "var(--v2-tile-blue-fg)",
  slate: "var(--v2-dark)",
};
const PRIORITY_STYLE = {
  Critical: { background: "var(--v2-red-tint)",   color: "var(--v2-red)"   },
  High:     { background: "var(--v2-amber-tint)", color: "var(--v2-amber)" },
  Ready:    { background: "var(--v2-green-tint)", color: "var(--v2-green)" },
};
const CAT_LUCIDE = { Decision: Scale, Issue: TriangleAlert, Action: CircleCheck, Signal: RadioTower, Project: FolderKanban, Expiration: Clock, Watch: RadioTower, Insight: Lightbulb };

// Type → accent color for the In Focus (Option B) tile eyebrow/watermark.
// Exported so the drawer header chip can reference the same constant,
// keeping tile and drawer colors in sync (single source of truth).
// NOTE: "Signal" and "Project" are legacy/mismatched category values still
// produced by some data paths (see PULSE-INFOCUS-TILE-B-S1 out-of-scope note);
// they fall back to the Watch treatment until the source mapping is fixed.
export const INFOCUS_TYPE_COLOR = {
  Decision:   "var(--v2-accent)",
  Issue:      "var(--v2-red)",
  Action:     "var(--v2-teal, var(--v2-green))",
  Expiration: "var(--v2-amber)",
  Watch:      "var(--v2-mid)",
  Signal:     "var(--v2-mid)",
  Project:    "var(--v2-mid)",
  Insight:    "var(--v2-accent)",
};
function typeColor(cat) { return INFOCUS_TYPE_COLOR[cat] ?? INFOCUS_TYPE_COLOR.Watch; }
function typeIcon(cat) { return CAT_LUCIDE[cat] ?? RadioTower; }
const EVENT_DOT    = { red: "var(--v2-red)", amber: "var(--v2-amber)", default: "var(--v2-accent)" };
const EVENT_RING   = { red: "var(--v2-red-tint)", amber: "var(--v2-amber-tint)", default: "var(--v2-blue-tint)" };

/* ─── Drawer formatter helpers ───────────────────────────────────────────── */

function formatDrawerRent(annualRentUsd) {
  if (annualRentUsd == null) return "—";
  const n = Number(annualRentUsd);
  if (!Number.isFinite(n) || n <= 0) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M/yr`;
  if (n >= 1_000)     return `$${Math.round(n / 1_000)}K/yr`;
  return `$${Math.round(n)}/yr`;
}

const MONTH_ABBR_DRAWER = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

/** Formats ISO date "YYYY-MM-DD" as "MMM DD 'YY · Nd" (with days suffix when daysLeft provided). */
function formatDrawerExpiry(isoDate, daysLeft) {
  if (!isoDate) return "—";
  const d = new Date(`${String(isoDate).slice(0, 10)}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return "—";
  const mo  = MONTH_ABBR_DRAWER[d.getUTCMonth()];
  const dy  = d.getUTCDate();
  const yr  = String(d.getUTCFullYear()).slice(2);
  const base = `${mo} ${dy} '${yr}`;
  if (daysLeft == null) return base;
  return `${base} · ${daysLeft}d`;
}

/* ─── Shared sub-components ─────────────────────────────────────────────── */

function SectionHeader({ title, sub, tooltip, tooltipTestId }) {
  const [tipPos, setTipPos] = useState(null);
  const iconRef = useRef(null);
  const structuredTooltip = typeof tooltip !== "string";
  const intent = useHoverIntent({ onOpen: handleMouseEnter });

  function handleMouseEnter() {
    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      setTipPos({ top: rect.bottom + 6, left: rect.left + rect.width / 2 });
    }
  }

  return (
    <>
      <div data-testid="v2-pulse-section-title" style={{ display: "flex", alignItems: "center", gap: 8, fontSize: T.fsBase, fontWeight: T.fwBold }}>
        <span style={{ display: "inline-block", width: 3, height: 14, background: "var(--v2-accent)", borderRadius: 2 }} />
        {title}
        {tooltip && (
          <span
            data-testid={tooltipTestId}
            style={{ position: "relative", display: "inline-flex", alignItems: "center" }}
            {...intent.triggerProps}
            tabIndex={0}
          >
            <Info ref={iconRef} size={14} style={{ color: "var(--v2-mid)", cursor: "help" }} />
            {intent.open && tipPos && ReactDOM.createPortal(
              <span role="tooltip" style={{
                position: "fixed", top: tipPos.top, left: tipPos.left, transform: "translateX(-50%)",
                background: "var(--v2-tooltip-bg)", color: "var(--v2-tooltip-fg)",
                fontSize: T.tooltip.fontSize, fontWeight: T.tooltip.fontWeight,
                padding: structuredTooltip ? "12px 14px" : "7px 11px",
                 borderRadius: structuredTooltip ? 8 : 6, whiteSpace: structuredTooltip ? "normal" : "pre-line",
                 width: structuredTooltip ? 430 : 260,
                 maxWidth: structuredTooltip ? "min(430px, calc(100vw - 24px))" : 260,
                 lineHeight: T.tooltip.lineHeight, zIndex: 9999,
                pointerEvents: "none",
              }}>
                 {structuredTooltip ? tooltip : <TooltipParagraphs>{tooltip}</TooltipParagraphs>}
              </span>,
              document.body
            )}
          </span>
        )}
      </div>
      {sub && <div style={{ fontSize: T.fsBase, color: "var(--v2-mid)", marginTop: 2 }}>{sub}</div>}
    </>
  );
}

function Card({ children, style, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        background: "var(--v2-card)",
        border: "1px solid var(--v2-line)",
        borderRadius: 16,
        boxShadow: "var(--v2-shadow-card)",
        padding: "18px 20px",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function SegBtn({ options, value, onChange, style: styleProp }) {
  return (
    <div style={{ display: "inline-flex", background: "var(--v2-hair)", borderRadius: 10, padding: 3, margin: "12px 0 14px", ...styleProp }}>
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          style={{
            border: 0, padding: "6px 12px", borderRadius: 8,
            fontSize: T.fsBase, fontWeight: T.fwBold, cursor: "pointer",
            background: value === opt.value ? "var(--v2-card)" : "transparent",
            color: value === opt.value ? "var(--v2-dark)" : "var(--v2-mid)",
            boxShadow: value === opt.value ? "0 1px 4px rgba(16,36,58,.1)" : "none",
            transition: "all .15s var(--v2-ease)",
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/* ─── Photo portrait card ────────────────────────────────────────────────── */

/**
 * PhotoPortraitCard — portrait card with a ~16:10 photo header band.
 * Shows a grey placeholder band when imageUrl is null (consistent 120 px height).
 */
export function PhotoPortraitCard({ card, score, imageUrl, onClick }) {
  const fg = TONE_FG[card.band];
  const duePillStyle =
    card.dueState === "over" || card.dueState === "soon"
      ? { background: TONE_BG[card.band], color: fg, border: `1px solid ${TONE_BORDER[card.band]}` }
      : null;

  return (
    <div
      data-testid={`v2-focus-card-${card.nm.replace(/\s+/g, "-")}`}
      onClick={onClick}
      style={{
        position: "relative",
        display: "flex", flexDirection: "column", minHeight: 212,
        borderRadius: 13,
        border: "1px solid var(--v2-line)",
        background: "var(--v2-card)", color: "var(--v2-dark)",
        cursor: "pointer",
        transition: "transform .15s var(--v2-ease), box-shadow .15s var(--v2-ease)",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 14px 30px rgba(16,36,58,.12)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}
    >
      {/* Fixed-height photo header band (120px) — grey placeholder when no image */}
      <div
        data-testid="v2-focus-card-photo-band"
        style={{ position: "relative", width: "100%", height: "120px", flexShrink: 0, overflow: "hidden", borderRadius: "13px 13px 0 0", background: "var(--v2-line)" }}
      >
        {imageUrl && (
          <img
            src={imageUrl}
            alt=""
            loading="lazy"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
          />
        )}
      </div>

      {/* Content area below photo (no watermark icon in photo mode) */}
      <div style={{ padding: "10px 13px 14px", display: "flex", flexDirection: "column", flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5, color: fg }}>
          <span style={{ fontSize: T.fsXs, fontWeight: T.fwBold, letterSpacing: ".06em", textTransform: "uppercase" }}>
            {card.cat}
          </span>
        </div>
        <div style={{ fontSize: T.fsBase, fontWeight: T.fwBold, marginTop: 7, lineHeight: 1.25, color: "var(--v2-dark)" }}>
          {card.nm}
        </div>
        <div style={{ fontSize: T.fsXs, color: "var(--v2-mid)", marginTop: 4, lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {card.issue ?? card.sm}
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10 }}>
          {duePillStyle ? (
            <span style={{ display: "inline-flex", alignItems: "center", fontSize: T.fsXs, fontWeight: T.fwSemibold, padding: "2px 10px", borderRadius: 6, ...duePillStyle, border: "none" }}>
              {`Due ${card.due}`}
            </span>
          ) : (
            <span style={{ fontSize: T.fsXs, color: "var(--v2-light)" }}>No date</span>
          )}
          <div style={{ display: "flex", alignItems: "baseline", gap: 2 }}>
            <span style={{ fontSize: T.fsLg, fontWeight: T.fwDisplay, lineHeight: 1, color: fg }}>{score}</span>
            <span style={{ fontSize: T.fsXs, color: "var(--v2-light)" }}>/ 100</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── In Focus tile — Option B (image left, location line, type in footer) ── */

/**
 * InFocusTileB — the enhanced "Option B" In Focus tile used on Pulse/home.
 * Left-side location image (falls back to a faint type watermark glyph when
 * no photo). Body is three stacked rows — muted uppercase location line
 * ("Market · Office"), one-line bold title, and a two-line grey description —
 * followed by a footer with the Due pill on the left and the color-coded
 * type label on the right (score is no longer shown; it still flows through
 * for sorting elsewhere). Presentation-only — the underlying card shape,
 * ranking, and click-through behavior are unchanged from PhotoPortraitCard.
 */
export function InFocusTileB({ card, score, imageUrl, onClick, marketsEnabled = true }) {
  const color = typeColor(card.cat);
  const Icon  = typeIcon(card.cat);
  const [imgBroken, setImgBroken] = useState(false);
  // Reset the broken flag whenever the URL changes (e.g. different card slot).
  useEffect(() => { setImgBroken(false); }, [imageUrl]);

  let dueLabel, duePillStyle;
  if (card.dueState === "over") {
    // Never prepend "Due" for overdue items — "Due Overdue" / "Due -Nd" are
    // unreadable. Show the server's "Overdue" string directly; if the server
    // ever sends a raw negative like "-694d", parse the magnitude and format
    // it as "694d overdue" so the label is always human-readable.
    const raw = card.due ?? "";
    if (/^-\d+d$/.test(raw)) {
      dueLabel = `${Math.abs(parseInt(raw, 10))}d overdue`;
    } else {
      dueLabel = raw || "Overdue";
    }
    duePillStyle = { background: TONE_BG.red, color: TONE_FG.red, border: `1px solid ${TONE_BORDER.red}` };
  } else if (card.dueState === "soon") {
    dueLabel = `Due ${card.due}`;
    duePillStyle = { background: TONE_BG.amber, color: TONE_FG.amber, border: `1px solid ${TONE_BORDER.amber}` };
  } else {
    dueLabel = card.due && card.due !== "No date" ? `Due ${card.due}` : "No date";
    duePillStyle = { background: "var(--v2-hair)", color: "var(--v2-light)", border: "1px solid var(--v2-line)" };
  }

  // Location line is "{Market} · {Office}". The office name is carried on
  // card.nm (server sends the location_alias there); card.sm is the item's
  // own short headline (e.g. "Renewal notice") and belongs in the title row.
  const locationLine = [marketsEnabled ? card.market : null, card.nm].filter(Boolean).join(" · ");

  return (
    <div
      data-testid={`v2-focus-card-${card.nm.replace(/\s+/g, "-")}`}
      onClick={onClick}
      style={{
        position: "relative",
        display: "flex", flexDirection: "row",
        // Fixed, content-independent size — flex: 0 0 <W>px (no grow, no
        // shrink) plus an explicit width, not "100%" of a possibly-flexible
        // parent, so long titles/descriptions can never widen the tile.
        flex: `0 0 ${INFOCUS_MINW}px`, width: INFOCUS_MINW, height: INFOCUS_TILE_H,
        borderRadius: 13,
        border: "1px solid var(--v2-line)",
        background: "var(--v2-card)", color: "var(--v2-dark)",
        cursor: "pointer",
        overflow: "hidden",
        boxShadow: "0 2px 8px rgba(16,36,58,.08)",
        transition: "transform .15s var(--v2-ease), box-shadow .15s var(--v2-ease)",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 14px 30px rgba(16,36,58,.12)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(16,36,58,.08)"; }}
    >
      {/* Full-height cover-strip image — left edge, ~35% width, fixed to the
          tile's fixed height so a late/undecoded image never resizes the
          card. Rounded only on the outer-left corners to match the card's
          own radius; the inner edge (against the body) stays square. Faint
          type watermark shows in place of the photo when there is no
          imageUrl. For Insight cards without a photo, a blue-tint accent
          background replaces the neutral grey placeholder. */}
      <div
        data-testid="v2-focus-card-photo-band"
        style={{
          width: "35%", minWidth: 90, height: INFOCUS_TILE_H, flexShrink: 0,
          overflow: "hidden",
          background: (card.cat === "Insight" && !imageUrl) ? "var(--v2-blue-tint)" : "var(--v2-tint)",
          display: "grid", placeItems: "center",
        }}
      >
        {imageUrl && !imgBroken ? (
          <img
            src={imageUrl}
            alt=""
            loading="lazy"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={() => setImgBroken(true)}
          />
        ) : (
          <Icon size={34} strokeWidth={1.4} style={{ color, opacity: card.cat === "Insight" ? 0.65 : 0.35 }} />
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0, height: INFOCUS_TILE_H, position: "relative", display: "flex", flexDirection: "column", padding: "9px 15px" }}>

        {/* Row 1 — location line: "Market · Office", muted uppercase, one line */}
        {locationLine && (
          <div style={{
            position: "relative",
            fontSize: T.fsXs, fontWeight: T.fwBold, letterSpacing: ".05em", textTransform: "uppercase",
            color: T.mid,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {locationLine}
          </div>
        )}

        {/* Row 2 — one-line bold title (the item's own headline), ellipsis on overflow */}
        <div style={{
          position: "relative", fontSize: T.fsBase, fontWeight: T.fwBold, marginTop: 4, lineHeight: 1.25, color: "var(--v2-dark)",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {card.sm}
        </div>

        {/* Row 3 — three-line grey description, ellipsis at end of line 3 */}
        <div style={{
          position: "relative",
          fontSize: T.fsXs, color: "var(--v2-mid)", marginTop: 5, lineHeight: 1.45,
          display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>
          {card.issue ?? ""}
        </div>

        <div style={{ flex: 1 }} />

        {/* Footer — Due pill left, color-coded type label right (score hidden from UI) */}
        <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 }}>
          <span style={{ display: "inline-flex", alignItems: "center", fontSize: T.fsXs, fontWeight: T.fwSemibold, padding: "2px 10px", borderRadius: 6, ...duePillStyle, border: "none" }}>
            {dueLabel}
          </span>
          {/* Watermark icon + category label: icon is absolutely centered behind
              the label, enlarged to ~3× for visual weight. Wrapper is relative
              so the oversized icon bleeds upward into the description area. */}
          <div style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Icon
              aria-hidden="true"
              size={60}
              strokeWidth={1.4}
              style={{ opacity: 0.045, color, pointerEvents: "none", position: "absolute", bottom: 0, left: "50%", transform: "translateX(-50%)", zIndex: 0 }}
            />
            <span style={{ fontSize: T.fsXs, fontWeight: T.fwBold, letterSpacing: ".05em", textTransform: "uppercase", color, position: "relative", zIndex: 1 }}>
              {card.cat}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * InFocusTileSkeleton — fixed-size loading placeholder matching InFocusTileB's
 * dimensions exactly, so slow data fetches never cause a layout shift or an
 * oddly-sized card flash while the row is loading.
 */
export function InFocusTileSkeleton() {
  return (
    <div
      data-testid="v2-focus-card-skeleton"
      style={{
        position: "relative",
        display: "flex", flexDirection: "row",
        // Same fixed, content-independent size as InFocusTileB — flex: 0 0
        // <W>px plus an explicit width — so the skeleton never differs in
        // size from the loaded tile it precedes.
        flex: `0 0 ${INFOCUS_MINW}px`, width: INFOCUS_MINW, height: INFOCUS_TILE_H,
        borderRadius: 13,
        border: "1px solid var(--v2-line)",
        background: "var(--v2-card)",
        overflow: "hidden",
      }}
    >
      <div style={{ width: "35%", minWidth: 90, height: INFOCUS_TILE_H, flexShrink: 0, background: "var(--v2-hair)" }} />
      <div style={{ flex: 1, minWidth: 0, height: INFOCUS_TILE_H, display: "flex", flexDirection: "column", padding: "9px 15px 7px", gap: 8 }}>
        <div style={{ height: 9, width: "45%", borderRadius: 4, background: "var(--v2-hair)" }} />
        <div style={{ height: 14, width: "80%", borderRadius: 4, background: "var(--v2-hair)", marginTop: 4 }} />
        <div style={{ height: 10, width: "95%", borderRadius: 4, background: "var(--v2-hair)", marginTop: 6 }} />
        <div style={{ height: 10, width: "70%", borderRadius: 4, background: "var(--v2-hair)" }} />
        <div style={{ flex: 1 }} />
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
          <div style={{ height: 16, width: 60, borderRadius: 999, background: "var(--v2-hair)" }} />
          <div style={{ height: 10, width: 50, borderRadius: 4, background: "var(--v2-hair)" }} />
        </div>
      </div>
    </div>
  );
}

/* ─── Section 1: In Focus — horizontally-scrollable carousel ─────────────── */

const INFOCUS_MINW   = 300;
const INFOCUS_GAP    = 14;
// INFOCUS_TILE_H is imported from pulseConstants.js so INFOCUS_SECTION_H
// can be derived from it in one place.

/**
 * InFocusTileRich — Direction 3 three-across card for the "rich" layout.
 *
 * Structure (top → bottom):
 *   ┌──────────────────────────────┐
 *   │ 84px image band              │  ← type chip top-left, due chip top-right
 *   ├──────────────────────────────┤
 *   │ MARKET · OFFICE              │  ← muted uppercase location line
 *   │ Bold headline title          │  ← 1-line ellipsis
 *   │ Issue body copy              │  ← 2-line clamp
 *   ├──────────────────────────────┤
 *   │ owner         urgency/100    │  ← footer bar
 *   └──────────────────────────────┘
 */
export function InFocusTileRich({ card, score, imageUrl, onClick, marketsEnabled = true }) {
  const color = typeColor(card.cat);
  const Icon  = typeIcon(card.cat);
  const [imgBroken, setImgBroken] = useState(false);
  // Reset the broken flag whenever the URL changes (e.g. different card slot).
  useEffect(() => { setImgBroken(false); }, [imageUrl]);

  let dueLabel, duePillStyle;
  if (card.dueState === "over") {
    const raw = card.due ?? "";
    dueLabel = /^-\d+d$/.test(raw) ? `${Math.abs(parseInt(raw, 10))}d overdue` : (raw || "Overdue");
    duePillStyle = { background: TONE_BG.red, color: TONE_FG.red, border: `1px solid ${TONE_BORDER.red}` };
  } else if (card.dueState === "soon") {
    dueLabel = `Due ${card.due}`;
    duePillStyle = { background: TONE_BG.amber, color: TONE_FG.amber, border: `1px solid ${TONE_BORDER.amber}` };
  } else {
    dueLabel = card.due && card.due !== "No date" ? `Due ${card.due}` : null;
    duePillStyle = { background: "var(--v2-hair)", color: "var(--v2-light)", border: "1px solid var(--v2-line)" };
  }

  const locationLine = [marketsEnabled ? card.market : null, card.nm].filter(Boolean).join(" · ");

  return (
    <div
      data-testid={`v2-focus-tile-rich-${card.nm.replace(/\s+/g, "-")}`}
      onClick={onClick}
      style={{
        display: "flex", flexDirection: "column",
        borderRadius: 13,
        border: "1px solid var(--v2-line)",
        background: "var(--v2-card)",
        cursor: "pointer",
        overflow: "hidden",
        boxShadow: "0 2px 8px rgba(16,36,58,.08)",
        transition: "transform .15s var(--v2-ease), box-shadow .15s var(--v2-ease)",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 14px 30px rgba(16,36,58,.12)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(16,36,58,.08)"; }}
    >
      {/* 84px image header band with overlaid chips */}
      <div style={{ position: "relative", height: 84, flexShrink: 0, background: "var(--v2-tint)", overflow: "hidden" }}>
        {imageUrl && !imgBroken ? (
          <img
            src={imageUrl}
            alt=""
            loading="lazy"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
            onError={() => setImgBroken(true)}
          />
        ) : (
          <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center" }}>
            <Icon size={34} strokeWidth={1.4} style={{ color, opacity: 0.35 }} />
          </div>
        )}
        {/* Type chip — top-left */}
        <span style={{
          position: "absolute", top: 8, left: 8,
          fontSize: T.fsXs, fontWeight: T.fwBold, letterSpacing: ".05em", textTransform: "uppercase",
          padding: "2px 10px", borderRadius: 6,
          background: "rgba(255,255,255,.88)", color,
          backdropFilter: "blur(4px)",
        }}>
          {card.cat}
        </span>
        {/* Due chip — top-right (only when there is a label to show) */}
        {dueLabel && (
          <span style={{
            position: "absolute", top: 8, right: 8,
            display: "inline-flex", alignItems: "center",
            fontSize: T.fsXs, fontWeight: T.fwSemibold,
            padding: "2px 10px", borderRadius: 6,
            backdropFilter: "blur(4px)",
            ...duePillStyle, border: "none",
          }}>
            {dueLabel}
          </span>
        )}
      </div>

      {/* Body */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "10px 13px 0" }}>
        {locationLine && (
          <div style={{
            fontSize: T.fsXs, fontWeight: T.fwBold, letterSpacing: ".05em", textTransform: "uppercase",
            color: T.mid,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            marginBottom: 3,
          }}>
            {locationLine}
          </div>
        )}
        <div style={{
          fontSize: T.fsBase, fontWeight: T.fwBold, lineHeight: 1.25, color: "var(--v2-dark)",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          marginBottom: 4,
        }}>
          {card.sm}
        </div>
        <div style={{
          fontSize: T.fsXs, color: "var(--v2-mid)", lineHeight: 1.45,
          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>
          {card.issue ?? ""}
        </div>
        <div style={{ flex: 1 }} />
      </div>

      {/* Footer bar */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "8px 13px 10px",
        borderTop: "1px solid var(--v2-line)",
        marginTop: 8,
      }}>
        <span style={{ fontSize: T.fsXs, color: "var(--v2-light)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "60%" }}>
          {card.owner ?? ""}
        </span>
        {score != null && (
          <span style={{ fontSize: T.fsXs, fontWeight: T.fwDisplay, color }}>
            {score}<span style={{ fontWeight: T.fwSemibold, color: "var(--v2-light)" }}>/100</span>
          </span>
        )}
      </div>
    </div>
  );
}

// ── DismissedGroups ────────────────────────────────────────────────────────────
// Renders the dismissed area below the carousel as two independently-collapsible
// labelled groups: Acknowledged and Snoozed. Each group is hidden when empty.

// Pill bg/color map for the dismissed-row type pill (mirrors InFocusRadarPage TYPE_CHIP).
const DISMISSED_PILL_STYLE = {
  Decision: { bg: "var(--v2-blue-tint)",  color: "var(--v2-accent)"       },
  Issue:    { bg: "var(--v2-red-tint)",   color: "var(--v2-tile-red-fg)"  },
  Action:   { bg: "var(--v2-teal-tint,var(--v2-green-tint))", color: "var(--v2-teal, var(--v2-green))" },
  Watch:    { bg: "var(--v2-hair)",       color: "var(--v2-mid)"          },
};

/** Returns a short relative time string like "2 days ago" from an ISO timestamp. */
function relativeTime(isoStr) {
  if (!isoStr) return "just now";
  const ms = Date.now() - new Date(isoStr).getTime();
  const days = Math.floor(ms / 86_400_000);
  if (days < 1) {
    const hours = Math.floor(ms / 3_600_000);
    return hours < 1 ? "just now" : `${hours}h ago`;
  }
  if (days === 1) return "1 day ago";
  if (days < 30)  return `${days} days ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? "1 month ago" : `${months} months ago`;
}

export function DismissedCardRow({ card, onRestore, currentRole = "viewer" }) {
  const isAcknowledged = card.dismissedAction === "acknowledged";
  const canRestore = (currentRole === "editor" || currentRole === "admin")
    && typeof onRestore === "function";

  if (isAcknowledged) {
    // Enriched layout — shows type pill, name/object/why, key meta, and Restore ↩
    const pill    = DISMISSED_PILL_STYLE[card.cat] ?? DISMISSED_PILL_STYLE.Watch;
    const whyText = card.issue ?? card.sm;

    return (
      <div
        data-testid={`infocus-dismissed-row-${(card.nm ?? "").replace(/\s+/g, "-")}`}
        style={{
          display: "flex", gap: 12, padding: "13px 16px",
          borderBottom: "1px solid var(--v2-hair)", alignItems: "flex-start",
        }}
      >
        {/* Type pill */}
        <span style={{
          display: "inline-flex", alignItems: "center",
          fontSize: T.fsXs, fontWeight: T.fwBold, letterSpacing: ".04em", textTransform: "uppercase",
          padding: "3px 8px", borderRadius: 6, flexShrink: 0, marginTop: 1,
          background: pill.bg, color: pill.color,
        }}>
          {card.cat}
        </span>

        {/* Middle: name / object / why / meta */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: "var(--v2-dark)" }}>
            {card.nm}
          </div>
          {card.sm && (
            <div style={{ fontSize: T.fsXs, color: "var(--v2-light)", marginTop: 1 }}>
              {card.sm}
            </div>
          )}
          {whyText && (
            <div style={{ fontSize: T.fsBase, color: "var(--v2-mid)", marginTop: 5 }}>
              {whyText}
            </div>
          )}
          {/* Meta row — only items with a non-null value */}
          {(card.due || card.cost != null || card.dueState) && (
            <div style={{ display: "flex", gap: 14, marginTop: 6, flexWrap: "wrap" }}>
              {card.due && (
                <span style={{ fontSize: T.fsXs, color: "var(--v2-mid)" }}>
                  Notice due <b style={{ color: "var(--v2-dark)", fontWeight: T.fwSemibold }}>{card.due}</b>
                </span>
              )}
              {card.cost != null && (
                <span style={{ fontSize: T.fsXs, color: "var(--v2-mid)" }}>
                  Rent <b style={{ color: "var(--v2-dark)", fontWeight: T.fwSemibold }}>{formatDrawerRent(card.cost)}</b>
                </span>
              )}
              {card.dueState && (
                <span style={{ fontSize: T.fsXs, color: "var(--v2-mid)" }}>
                  Outlook{" "}
                  <b style={{
                    color: card.dueState === "over" ? "var(--v2-red)" : "var(--v2-dark)",
                    fontWeight: T.fwSemibold,
                  }}>
                    {card.dueState}
                  </b>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Right: Restore ↩ + dismissed-by attribution */}
        <div style={{ flexShrink: 0, textAlign: "right" }}>
          {canRestore && (
            <button
              data-testid={`infocus-restore-${(card.nm ?? "").replace(/\s+/g, "-")}`}
              onClick={() => onRestore(card.cardKey)}
              style={{
                background: "none", border: "none", padding: 0,
                fontSize: T.fsSm, fontWeight: T.fwBold,
                color: "var(--v2-accent)", cursor: "pointer", whiteSpace: "nowrap",
              }}
            >
              Restore ↩
            </button>
          )}
          {(card.dismissedBy || card.dismissedAt) && (
            <div style={{ fontSize: T.fsXs, color: "var(--v2-light)", marginTop: 6, maxWidth: 130 }}>
              {card.dismissedBy ? `Dismissed by ${card.dismissedBy}` : "Dismissed"}
              {card.dismissedAt && <><br />{relativeTime(card.dismissedAt)}</>}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Snoozed: compact layout
  return (
    <div
      key={card.cardKey ?? card.nm}
      data-testid={`infocus-dismissed-row-${(card.nm ?? "").replace(/\s+/g, "-")}`}
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: "var(--v2-hair)", borderRadius: 8, padding: "7px 12px",
        gap: 10,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{
          fontSize: T.fsXs, fontWeight: T.fwBold, color: typeColor(card.cat),
          textTransform: "uppercase", letterSpacing: ".04em", marginRight: 6,
        }}>
          {card.cat}
        </span>
        <span style={{ fontSize: T.fsBase, fontWeight: T.fwSemibold, color: "var(--v2-dark)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {card.nm}
        </span>
        <span style={{ fontSize: T.fsXs, color: "var(--v2-light)", marginLeft: 8 }}>
          {card.snoozedUntil ? `Snoozed until ${card.snoozedUntil}` : "Dismissed"}
        </span>
      </div>
      {canRestore && (
        <button
          data-testid={`infocus-restore-${(card.nm ?? "").replace(/\s+/g, "-")}`}
          onClick={() => onRestore(card.cardKey)}
          style={{
            background: "none", border: "1px solid var(--v2-line)", borderRadius: 6,
            padding: "3px 9px", fontSize: T.fsXs, fontWeight: T.fwSemibold,
            color: "var(--v2-mid)", cursor: "pointer", whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          Restore
        </button>
      )}
    </div>
  );
}

export function DismissedGroup({ testId, label, subtitle, cards, onRestore, currentRole = "viewer" }) {
  const [open, setOpen] = useState(false);
  if (!cards.length) return null;
  return (
    <div style={{ marginTop: 10 }}>
      <button
        data-testid={testId}
        onClick={() => setOpen(o => !o)}
        style={{
          background: "none", border: "none", padding: 0,
          fontSize: T.fsXs, fontWeight: T.fwSemibold,
          color: "var(--v2-mid)", cursor: "pointer",
          display: "flex", alignItems: "center", gap: 5,
        }}
      >
        <span style={{
          display: "inline-block", width: 16, height: 16,
          borderRadius: 999, background: "var(--v2-hair)",
          fontSize: T.fsXs, fontWeight: T.fwBold,
          textAlign: "center", lineHeight: "16px", color: "var(--v2-mid)",
        }}>
          {cards.length}
        </span>
        {label}
        <span style={{ fontSize: T.fsXs, color: "var(--v2-light)", fontWeight: T.fwNormal }}>{subtitle}</span>
        <span style={{ fontSize: T.fsXs, color: "var(--v2-light)" }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
          {cards.map(card => (
            <DismissedCardRow
              key={card.cardKey ?? card.nm}
              card={card}
              onRestore={onRestore}
              currentRole={currentRole}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function DismissedGroups({ dismissedCards, onRestore, currentRole = "viewer" }) {
  const acknowledged = dismissedCards.filter(c => c.dismissedAction === "acknowledged");
  const snoozed      = dismissedCards.filter(c => c.dismissedAction === "snoozed");
  if (!acknowledged.length && !snoozed.length) return null;
  return (
    <div style={{ marginTop: 14, borderTop: "1px solid var(--v2-line)", paddingTop: 10 }}>
      <DismissedGroup
        testId="infocus-dismissed-section"
        label="Dismissed"
        subtitle="Hidden from In Focus · Restore to bring back"
        cards={acknowledged}
        onRestore={onRestore}
        currentRole={currentRole}
      />
      <DismissedGroup
        testId="infocus-snoozed-section"
        label="Snoozed"
        subtitle="Returns on scheduled date"
        cards={snoozed}
        onRestore={onRestore}
        currentRole={currentRole}
      />
    </div>
  );
}

// ── InFocusSection ─────────────────────────────────────────────────────────────

function InFocusSection({
  onCardClick,
  locationRisk,
  onNavigate,
  isLoading,
  inFocusLayout = 'classic',
  radarEnabled = true,
  marketsEnabled = true,
}) {
  const [visibleN, setVisibleN]         = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const outerRef = useRef(null);

  // 'off' layout: render nothing (new kill-switch replaces FEATURE_IN_FOCUS_PANEL).
  if (inFocusLayout === 'off') return null;

  const liveCards = locationRisk?.cards;
  const cards  = liveCards ?? [];
  const sorted = [...cards].sort((a, b) => b.urgency - a.urgency);
  const hidden = visibleN > 0 ? Math.max(0, cards.length - visibleN) : 0;

  // Derived arrow states from index position.
  const canScrollLeft  = currentIndex > 0;
  const canScrollRight = visibleN > 0 && currentIndex + visibleN < sorted.length;

  // ResizeObserver on the outer wrapper — used only to compute visibleN for
  // the "View all →" count.  Layout is now driven by index slice, not scroll.
  // Debounce (16ms = one animation frame) so rapid resize events during a
  // drag-resize don't interrupt a running slide animation mid-frame.
  useLayoutEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    let timer;
    const ro = new ResizeObserver((entries) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const w = entries[0].contentRect.width;
        setVisibleN(Math.max(1, Math.floor((w + INFOCUS_GAP) / (INFOCUS_MINW + INFOCUS_GAP))));
      }, 16);
    });
    ro.observe(el);
    return () => { ro.disconnect(); clearTimeout(timer); };
  }, []);

  const navigateCarousel = (dir) => {
    setCurrentIndex((prev) => {
      const next = prev + dir * Math.max(1, visibleN);
      return Math.max(0, Math.min(next, Math.max(0, sorted.length - Math.max(1, visibleN))));
    });
  };

  const visibleCards = sorted.length > 0
    ? (visibleN === 0 ? sorted : sorted.slice(currentIndex, currentIndex + visibleN))
    : [];

  return (
    <Card
      testId="v2-section-infocus"
      style={{
        background: "linear-gradient(180deg, var(--v2-blue-tint) 0%, var(--v2-infocus-wash-end) 100%)",
        borderColor: "var(--v2-infocus-border)",
        boxShadow: "var(--v2-shadow-pane)",
        minHeight: INFOCUS_SECTION_H,
        overflow: "hidden",
      }}
    >
      {/* Header row: title + arrow controls */}
      <div style={{ marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <SectionHeader
          title="In focus"
          tooltip="Engine-ranked across decisions, issues, actions, signals and projects. Shows the highest-urgency items across the portfolio. Click any card to open its detail."
        />
        <div style={{ display: "flex", gap: 6 }}>
          <CarouselArrowButton direction="previous" enabled={canScrollLeft} testId="infocus-arrow-left" label="Scroll left" onClick={() => navigateCarousel(-1)} />
          <CarouselArrowButton direction="next" enabled={canScrollRight} testId="infocus-arrow-right" label="Scroll right" onClick={() => navigateCarousel(1)} />
        </div>
      </div>

      {/* Carousel strip — slot divs are keyed by index (stable); the inner div
          is keyed by card id so React swaps content instantly on navigation. */}
      <div ref={outerRef} style={{ overflow: "visible" }}>
        {isLoading ? (
          inFocusLayout === 'rich' ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: INFOCUS_GAP }}>
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} data-testid="infocus-slot-skeleton" style={{ borderRadius: 13, border: "1px solid var(--v2-line)", background: "var(--v2-card)", overflow: "hidden" }}>
                  <div style={{ height: 84, background: "var(--v2-hair)" }} />
                  <div style={{ padding: "10px 13px", display: "flex", flexDirection: "column", gap: 8 }}>
                    <div style={{ height: 9, width: "40%", borderRadius: 4, background: "var(--v2-hair)" }} />
                    <div style={{ height: 14, width: "75%", borderRadius: 4, background: "var(--v2-hair)" }} />
                    <div style={{ height: 10, width: "90%", borderRadius: 4, background: "var(--v2-hair)" }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ display: "flex", gap: INFOCUS_GAP }}>
              {Array.from({ length: Math.max(1, visibleN) }).map((_, i) => (
                <div
                  key={i}
                  data-testid="infocus-slot-skeleton"
                  style={{ flex: `0 0 ${INFOCUS_MINW}px`, display: "flex", position: "relative", overflow: "visible", borderRadius: 13 }}
                >
                  <div style={{ width: "100%" }}>
                    <InFocusTileSkeleton />
                  </div>
                </div>
              ))}
            </div>
          )
        ) : sorted.length === 0 ? (
          <div
            data-testid="infocus-empty"
            style={{ color: "var(--v2-mid)", fontSize: T.fsSm, padding: "16px 0" }}
          >
            No items in focus - check back as decisions, signals, and actions are logged.
          </div>
        ) : inFocusLayout === 'rich' ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: INFOCUS_GAP }}>
            {sorted.slice(0, 3).map((card) => (
              <InFocusTileRich
                key={card.nm}
                card={card}
                score={card.urgency}
                imageUrl={card.imageUrl ?? null}
                marketsEnabled={marketsEnabled}
                onClick={() => onCardClick(card)}
              />
            ))}
          </div>
        ) : (
          <div style={{ display: "flex", gap: INFOCUS_GAP }}>
            {visibleCards.map((card, slotIdx) => (
              <div
                key={slotIdx}
                data-testid="infocus-slot"
                style={{ flex: `0 0 ${INFOCUS_MINW}px`, display: "flex", position: "relative", overflow: "visible", borderRadius: 13 }}
              >
                <div key={card.nm} style={{ width: "100%" }}>
                  <InFocusTileB
                    card={card}
                    score={card.urgency}
                    imageUrl={card.imageUrl ?? null}
                    marketsEnabled={marketsEnabled}
                    onClick={() => onCardClick(card)}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* "View all →" — shown when cards overflow the visible strip width.
          INFOCUS_SECTION_H reserves enough height for this footer row from
          first paint, so it fills into the pre-reserved space without pushing
          content below the card when it appears. */}
      {radarEnabled && hidden > 0 && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
          <button
            data-testid="infocus-view-all"
            onClick={() => { if (onNavigate) onNavigate("?view=infocus-radar"); }}
            style={{
              ...OUTBOUND_LINK_STYLE,
              whiteSpace: "nowrap",
            }}
          >
            {`View all ${cards.length} items `}
            <span style={{ color: "var(--v2-accent)" }}>→</span>
          </button>
        </div>
      )}

    </Card>
  );
}

/* ─── Section 3: Attendance ─────────────────────────────────────────────── */

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri"];
const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

const ATTENDANCE_CHART_TOOLTIP_STYLE = {
  fontSize: T.tooltip.fontSize,
  fontWeight: T.tooltip.fontWeight,
  lineHeight: T.tooltip.lineHeight,
  borderRadius: 8,
  border: "none",
  backgroundColor: T.tooltip.bg,
  color: T.tooltip.fg,
  boxShadow: T.tooltip.shadow,
  padding: 10,
};

function attendanceCoverageLine(reportingOfficeCount, scopeOfficeCount) {
  if (reportingOfficeCount == null || scopeOfficeCount == null) return null;
  return `${reportingOfficeCount} of ${scopeOfficeCount} offices reporting`;
}

export function AttendanceTrendTooltip({ active, payload, label, mode = "percent" }) {
  if (!active || !payload?.length) return null;
  const month = payload[0].payload;
  const coverageLine = attendanceCoverageLine(month?.reportingOfficeCount, month?.scopeOfficeCount);
  return (
    <div style={ATTENDANCE_CHART_TOOLTIP_STYLE}>
      <div style={{ fontWeight: T.fwBold, marginBottom: 4 }}>{label}</div>
      {payload.map((metric) => (
        <div key={metric.dataKey}>{metric.name}: {metric.value == null ? "—" : mode === "count" ? Math.round(metric.value).toLocaleString("en-US") : `${metric.value}%`}</div>
      ))}
      {coverageLine && <div>{coverageLine}</div>}
    </div>
  );
}

export function AttendanceWeeklyTooltip({ active, payload, label, mode = "percent" }) {
  if (!active || !payload?.length) return null;

  const metric = payload[0];
  const value = metric?.value;
  return (
    <div style={ATTENDANCE_CHART_TOOLTIP_STYLE}>
      <div style={{ fontWeight: T.fwBold, marginBottom: 4 }}>{label}</div>
      <div>{metric?.name ?? "All badge-ins rate"}: {value == null ? "—" : mode === "count" ? Math.round(value).toLocaleString("en-US") : `${value}%`}</div>
    </div>
  );
}

function fmtAttendanceCost(value) {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  const amount = Number(value);
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) return `$${Math.round(amount / 1_000)}K`;
  return `$${Math.round(amount).toLocaleString()}`;
}

function fmtAttendanceMonth(value) {
  const text = String(value ?? "").trim();
  const monthName = text.match(/\b(January|February|March|April|May|June|July|August|September|October|November|December)\b/i);
  if (monthName) return monthName[1].slice(0, 3);
  const monthNumber = text.match(/^\d{4}[-/](\d{1,2})/);
  if (monthNumber) {
    const month = Number(monthNumber[1]);
    if (month >= 1 && month <= 12) return new Date(Date.UTC(2025, month - 1, 1)).toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
  }
  return text.slice(0, 3);
}

function fmtAttendanceRange(value) {
  return String(value ?? "").replace(/[–—]/g, " to ");
}

function formatAttendanceWeekRange(start, end) {
  if (!start || !end) return null;
  const formatDate = (value) => {
    const date = new Date(`${String(value).slice(0, 10)}T00:00:00Z`);
    if (Number.isNaN(date.getTime())) return null;
    return {
      month: date.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" }),
      day: date.getUTCDate(),
      year: date.getUTCFullYear(),
    };
  };
  const first = formatDate(start);
  const last = formatDate(end);
  if (!first || !last) return null;
  const startLabel = `${first.month} ${first.day}`;
  const endLabel = `${last.month !== first.month ? `${last.month} ` : ""}${last.day}, ${last.year}`;
  return `${startLabel}–${endLabel}`;
}

export function AttendancePane({ sharedAttendance, sharedLoading, sharedError }) {
  const [scope, setScope] = useState({ type: "all", value: null });
  const [attendanceMode, setAttendanceMode] = useState("count");
  const [selectedWeekStart, setSelectedWeekStart] = useState(null);
  const [weekMenuOpen, setWeekMenuOpen] = useState(false);
  const weekPickerRef = useRef(null);
  const [scopedAttendance, setScopedAttendance] = useState(null);
  const [scopedLoading, setScopedLoading] = useState(false);
  const [scopedError, setScopedError] = useState(false);

  useEffect(() => {
    if (!weekMenuOpen) return undefined;
    const closeOutside = (event) => {
      if (!weekPickerRef.current?.contains(event.target)) setWeekMenuOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setWeekMenuOpen(false);
        weekPickerRef.current?.querySelector("button")?.focus();
      }
    };
    document.addEventListener("mousedown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [weekMenuOpen]);

  useEffect(() => {
    // The shared response owns the all-location scope. Historical week
    // selection is local to Weekly Pattern; only a location scope needs a
    // pane-specific attendance request.
    if (scope.type === "all") return undefined;
    let cancelled = false;
    setScopedLoading(true);
    setScopedError(false);
    getPulseAttendance({ scopeType: scope.type, scopeValue: scope.value })
      .then((result) => {
        if (!cancelled) {
          setScopedAttendance(result ?? null);
          setSelectedWeekStart(null);
        }
      })
      .catch(() => { if (!cancelled) setScopedError(true); })
      .finally(() => { if (!cancelled) setScopedLoading(false); });
    return () => { cancelled = true; };
  }, [scope.type, scope.value]);

  const usesSharedAllLocations = scope.type === "all";
  // Keep the last visible scope response while a new scope is loading, even
  // when the first picker change is from All locations.
  const attendance = usesSharedAllLocations ? sharedAttendance : scopedAttendance ?? sharedAttendance;
  const pickerScopeOptions = scope.type === "region" ? attendance?.scopes?.regions
    : scope.type === "market" ? attendance?.scopes?.markets
      : attendance?.scopes?.offices;
  const pickerSelectedOffice = scope.type === "office"
    ? pickerScopeOptions?.find((option) => option.value === scope.value)
    : null;
  const pickerScopeLabel = scope.type === "all" ? "All locations"
    : pickerSelectedOffice
      ? <OfficeLocationLabel office={pickerSelectedOffice} />
      : pickerScopeOptions?.find((option) => option.value === scope.value)?.label ?? scope.value;
  const loading = usesSharedAllLocations ? sharedLoading : scopedLoading && !attendance;
  const error = usesSharedAllLocations ? sharedError : scopedError && !attendance;
  const months = attendance?.months ?? [];
  // Suppress only leading thin months. Later gaps and thin months retain their
  // chronological positions; single-office scopes need just one report.
  const firstDataMonthIndex = months.findIndex((month) => (
    (month.avgPct != null || month.peakPct != null)
    && (month.scopeOfficeCount == null
      || (month.reportingOfficeCount ?? 0) >= Math.max(1, Math.ceil(month.scopeOfficeCount / 2)))
  ));
  const chartMonths = firstDataMonthIndex >= 0 ? months.slice(firstDataMonthIndex) : [];
  const latest = attendance?.latestCompleteMonth
    ?? chartMonths.at(-1)
    ?? null;
  const weeks = attendance?.weeks ?? [];
  const selectedWeek = selectedWeekStart ? weeks.find((week) => week.weekStart === selectedWeekStart) : null;
  const weekData = selectedWeek ?? attendance?.typicalWeek;
  const countValue = (value) => value == null || !Number.isFinite(Number(value))
    ? null
    : Math.round(Number(value));
  const averageCount = (total, periods) => total == null || periods == null
    || !Number.isFinite(Number(total)) || !Number.isFinite(Number(periods)) || Number(periods) <= 0
    ? null
    : countValue(Number(total) / Number(periods));
  const values = (weekData?.days ?? []).map((day) => attendanceMode === "count"
    ? selectedWeek ? countValue(day.attendanceCount) : averageCount(day.attendanceCount, day.coveredWeeks)
    : day.valuePct);
  const peak = values.filter((value) => value != null).length ? Math.max(...values.filter((value) => value != null)) : null;
  const pooledAttendanceAverage = latest?.attendanceCount != null
    && Number(latest.coveredDays) > 0
    ? Number(latest.attendanceCount) / Number(latest.coveredDays)
    : attendance?.averageDayAttended;
  const rawAverageDayAttended = pooledAttendanceAverage;
  const averageDayAttended = rawAverageDayAttended == null ? null : Number(rawAverageDayAttended);
  const rawPeakDayAttended = latest?.peakAttendanceCount ?? attendance?.peakDayAttended;
  const peakDayAttended = rawPeakDayAttended == null ? null : Number(rawPeakDayAttended);
  const totalSeats = Number(attendance?.totalSeats);
  const rawAnnualPortfolioRent = attendance?.annualPortfolioRent;
  const annualPortfolioRent = rawAnnualPortfolioRent == null ? null : Number(rawAnnualPortfolioRent);
  const hasEfficiencyInputs = attendance?.hasData === true
    && Number.isFinite(totalSeats) && totalSeats > 0
    && Number.isFinite(averageDayAttended) && averageDayAttended >= 0
    && Number.isFinite(peakDayAttended) && peakDayAttended >= 0;
  const averageSeatShare = hasEfficiencyInputs
    ? Math.min(1, Math.max(0, averageDayAttended / totalSeats))
    : null;
  const peakSeatShare = hasEfficiencyInputs
    ? Math.min(1, Math.max(0, peakDayAttended / totalSeats))
    : null;
  const neverUsedSeats = hasEfficiencyInputs
    ? Math.max(totalSeats - peakDayAttended, 0)
    : null;
  const neverUsedShare = hasEfficiencyInputs ? neverUsedSeats / totalSeats : null;
  const displayAverageShare = averageSeatShare == null ? null : Math.min(1, Math.max(0, averageSeatShare));
  const displayPeakShare = peakSeatShare == null ? null : Math.max(displayAverageShare ?? 0, Math.min(1, Math.max(0, peakSeatShare)));
  const peakSwingShare = displayPeakShare == null
    ? null
    : Math.max(0, displayPeakShare - (displayAverageShare ?? 0));
  const displayNeverUsedShare = neverUsedShare == null
    ? null
    : Math.min(neverUsedShare, Math.max(0, 1 - (displayAverageShare ?? 0) - peakSwingShare));
  const neverUsedPct = neverUsedShare == null ? null : Math.round(neverUsedShare * 100);
  const averageSeatPct = averageSeatShare == null ? null : Math.round(averageSeatShare * 100);
  const peakSeatPct = peakSeatShare == null ? null : Math.round(peakSeatShare * 100);
  const averageSeatsUsed = countValue(averageDayAttended);
  const peakSeatsUsed = countValue(peakDayAttended);
  const neverUsedSeatsDisplay = hasEfficiencyInputs
    ? countValue(Math.max(totalSeats - peakDayAttended, 0))
    : null;
  const neverUsedRent = hasEfficiencyInputs
    ? neverUsedSeats * (annualPortfolioRent / totalSeats)
    : null;
  const efficiencyScopeLabel = attendance?.scope?.label ?? "All locations";
  const periodStatus = formatReportingPeriodStatus(
    attendance?.reportingPeriod,
    latest?.monthDate,
    { emptyShowsTarget: true },
  );
  const reportingPeriodLabel = periodStatus
    ? periodStatus.startsWith("As of ") ? periodStatus : `As of ${periodStatus}`
    : latest?.monthLabel
      ? `As of ${latest.monthLabel}`
      : attendance?.efficiencyReportingRange?.monthLabel
        ? `As of ${attendance.efficiencyReportingRange.monthLabel}`
        : "Reporting period unavailable";
  const efficiencyMonthLabel = reportingPeriodLabel;
  const efficiencyNoData = !loading && attendance?.hasData === true && !hasEfficiencyInputs;
  const reportingOfficeCount = attendance?.coverage?.reportingOfficeCount
    ?? attendance?.reportingOfficeCount
    ?? attendance?.reportingPeriod?.officesReported;
  const scopeOfficeCount = attendance?.coverage?.scopeOfficeCount
    ?? attendance?.scopeOfficeCount
    ?? attendance?.reportingPeriod?.totalOffices;
  const coverageLabel = attendanceCoverageLine(reportingOfficeCount, scopeOfficeCount);
  const attendancePeriodLabel = reportingPeriodLabel;
  const cardPeriodLabel = reportingPeriodLabel.replace(/^As of\s+/i, "· ");
  const officeDayCoverageSummary = latest?.presenceDataComplete === false
    && attendance?.reportingPeriod?.observedOfficeDays != null
    && attendance?.reportingPeriod?.eligibleOfficeDays != null
    ? `Attendance coverage this month: ${Number(attendance.reportingPeriod.observedOfficeDays).toLocaleString("en-US")} of ${Number(attendance.reportingPeriod.eligibleOfficeDays).toLocaleString("en-US")} eligible office-days observed.`
    : null;
  const displayedWeekRange = selectedWeek
    ? selectedWeek.weekStart && selectedWeek.weekEnd
      ? `${selectedWeek.weekStart} to ${selectedWeek.weekEnd}`
      : selectedWeek.weekStart
    : attendance?.typicalWeek?.coveredDateRange;
  const attendanceCoverageTooltip = (
    <div data-testid="attendance-pane-tooltip">
      <div data-testid="attendance-pane-tooltip-lead" style={{ color: T.tooltip.fg, fontSize: T.fsBase, lineHeight: 1.5 }}>
        <div data-testid="attendance-pane-tooltip-attendance" style={{ marginBottom: 7 }}>
          <strong>Attendance</strong> — of people assigned here, how many came in ·{" "}
          <em style={{ color: T.whiteMuted }}>are our people using the office?</em>
        </div>
        <div data-testid="attendance-pane-tooltip-utilization" style={{ marginBottom: 7 }}>
          <strong>Seat utilization</strong> — of seats installed, how many were used ·{" "}
          <em style={{ color: T.whiteMuted }}>are we using the space we pay for?</em>
        </div>
        <div data-testid="attendance-pane-tooltip-why-both" style={{ color: T.whiteMuted, fontSize: T.fsXs, lineHeight: 1.5, marginTop: 9 }}>
          <strong style={{ color: T.tooltip.fg }}>Same people counted two ways:</strong> attendance is the{" "}
          <strong style={{ color: T.tooltip.fg }}>people</strong> view, utilization the{" "}
          <strong style={{ color: T.tooltip.fg }}>real-estate</strong> view. We track both because one points at{" "}
          <strong style={{ color: T.tooltip.fg }}>policy</strong>, the other at{" "}
          <strong style={{ color: T.tooltip.fg }}>cost</strong> — they diverge when desks and headcount don’t match.
        </div>
      </div>
      <div
        data-testid="attendance-pane-tooltip-divider"
        aria-hidden="true"
        style={{ height: 1, background: "rgba(255,255,255,.15)", margin: "11px 0" }}
      />
      <div data-testid="attendance-pane-tooltip-details" style={{ color: T.whiteMuted, fontSize: T.fsXs, lineHeight: 1.55 }}>
        <div style={{ marginBottom: 6 }}>
          Both measures shown as average workday + peak · {attendancePeriodLabel}
          {coverageLabel ? ` (${coverageLabel})` : " (office reporting coverage unavailable)"}.
        </div>
        {officeDayCoverageSummary && (
          <div data-testid="attendance-pane-tooltip-day-coverage" style={{ marginBottom: 6 }}>
            {officeDayCoverageSummary}
          </div>
        )}
        <div style={{ marginBottom: 6 }}>Present = people badged in (Envoy; Alta/Verkada).</div>
        <div style={{ marginBottom: 6 }}>
          Company and country holidays are excluded. Missing office-days aren't treated as zero; averages use eligible office-days with observed attendance.
        </div>
        <div>Weekend badge-ins show in foot traffic, not this rate.</div>
      </div>
    </div>
  );
  const selectedIndex = selectedWeekStart ? weeks.findIndex((week) => week.weekStart === selectedWeekStart) : weeks.length;
  const stepWeek = (direction) => {
    if (!weeks.length) return;
    const nextIndex = selectedIndex + direction;
    if (nextIndex >= weeks.length) setSelectedWeekStart(null);
    else if (nextIndex < 0) setSelectedWeekStart(weeks[0].weekStart);
    else setSelectedWeekStart(weeks[nextIndex].weekStart);
  };
  const chartData = chartMonths.map((month) => ({
    month: month.monthLabel,
    avg: attendanceMode === "count"
      ? averageCount(month.attendanceCount, month.coveredDays)
      : month.avgPct,
    peak: attendanceMode === "count"
      ? countValue(month.peakAttendanceCount)
      : month.peakPct,
    reportingOfficeCount: month.reportingOfficeCount, scopeOfficeCount: month.scopeOfficeCount,
  }));
  const weekChartData = (weekData?.days ?? []).map((day, index) => ({
    ...day,
    label: DAY_LABELS[index],
    valuePct: values[index],
  }));
  const monthlyIntent = useChartHoverIntent(JSON.stringify([scope, attendanceMode, chartData]));
  const weeklyIntent = useChartHoverIntent(JSON.stringify([scope, attendanceMode, selectedWeekStart, weekChartData]));
  const latestAttendanceCount = attendanceMode === "count"
    ? averageCount(latest?.attendanceCount, latest?.coveredDays)
    : null;
  const sharedPresentCount = averageSeatsUsed
    ?? averageCount(latest?.attendanceCount, latest?.coveredDays);
  const assignedHeadcount = countValue(latest?.averageAssignedHeadcount);
  const peakSwingCount = averageDayAttended == null || peakDayAttended == null
    ? null
    : countValue(Math.max(peakDayAttended - averageDayAttended, 0));
  const reportingOfficeSentence = coverageLabel
    ? ` ${coverageLabel}.`
    : "";
  const attendanceBasisSentence = attendanceMode === "count"
    ? `${sharedPresentCount != null && assignedHeadcount != null
      ? `On an average workday, about ${sharedPresentCount.toLocaleString("en-US")} of the ${assignedHeadcount.toLocaleString("en-US")} people assigned came in.`
      : assignedHeadcount != null
        ? `Average workday attendance among ${assignedHeadcount.toLocaleString("en-US")} assigned people.`
        : "Average workday attendance."}${reportingOfficeSentence}`
    : assignedHeadcount != null
      ? `${latest?.avgPct != null
        ? `On an average workday, ${Math.round(latest.avgPct)}% of the ${assignedHeadcount.toLocaleString("en-US")} assigned people came in${sharedPresentCount != null ? ` - about ${sharedPresentCount.toLocaleString("en-US")} people` : ""}.`
        : "Attendance was unavailable on an average workday."}${reportingOfficeSentence}`
      : `${latest?.avgPct != null
        ? `On an average workday, ${Math.round(latest.avgPct)}% of assigned people came in${sharedPresentCount != null ? ` - about ${sharedPresentCount.toLocaleString("en-US")} people` : ""}.`
        : "Attendance was unavailable on an average workday."}${reportingOfficeSentence}`;
  const utilizationBasisSentence = averageSeatsUsed == null
    ? "Seat utilization uses the same pooled presence as attendance, measured against installed seats."
    : attendanceMode === "count"
      ? `Those same ~${averageSeatsUsed.toLocaleString("en-US")} people filled ${averageSeatsUsed.toLocaleString("en-US")} of ${totalSeats.toLocaleString("en-US")} installed seats on an average workday; ${peakSeatsUsed?.toLocaleString("en-US") ?? "—"} at peak.`
      : `On an average workday, ${averageSeatPct}% of the ${totalSeats.toLocaleString("en-US")} installed seats were used - the same ~${averageSeatsUsed.toLocaleString("en-US")} people; ${peakSeatPct}% at peak.`;
  const efficiencyLegendValues = attendanceMode === "count"
    ? [
      `${averageSeatsUsed?.toLocaleString("en-US") ?? "—"} seats`,
      `${peakSwingCount?.toLocaleString("en-US") ?? "—"} seats`,
      `${neverUsedSeatsDisplay?.toLocaleString("en-US") ?? "—"} seats`,
    ]
    : [
      `${Math.round((displayAverageShare ?? 0) * 100)}%`,
      `${Math.round((peakSwingShare ?? 0) * 100)}%`,
      `${Math.round((displayNeverUsedShare ?? 0) * 100)}%`,
    ];
  const noData = !loading && (!attendance || attendance.hasData === false);
  const peakDayIndex = values.findIndex((value) => value != null && value === peak);
  const peakDayLabel = peakDayIndex >= 0 ? DAY_NAMES[peakDayIndex] : null;

  return (
    <div data-testid="v2-section-attendance">
    <Card testId="v2-section-util-trend" style={{ minHeight: PULSE_PANE_H, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 18 }}>
          <SectionHeader
            title="Attendance & utilization"
            tooltip={attendanceCoverageTooltip}
            tooltipTestId="attendance-pane-info-trigger"
          />
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <LocationScopePicker data={attendance} scopeType={scope.type} scopeValue={scope.value} selectedLabel={pickerScopeLabel} onSelect={(type, value) => {
              setScope({ type, value });
              if (type === "all") setSelectedWeekStart(null);
            }} />
            <ToolbarViewToggle
              ariaLabel="Attendance display"
              testId="attendance-display-toggle"
              value={attendanceMode}
              onChange={setAttendanceMode}
              compact
              segments={[
                { key: "count", label: "#", testid: "attendance-display-count" },
                { key: "percent", label: "%", testid: "attendance-display-percent" },
              ]}
            />
            {scope.type !== "all" && attendance && (scopedLoading || scopedError) && (
              <span
                data-testid="attendance-scope-refresh-status"
                role="status"
                style={{ color: "var(--v2-mid)", fontSize: T.fsXs }}
              >
                {scopedLoading ? "Updating…" : "Update failed"}
              </span>
            )}
        </div>
      </div>
       {loading ? (
        <div data-testid="attendance-loading" style={{ minHeight: 220, display: "grid", placeItems: "center", color: "var(--v2-mid)", fontSize: T.fsBase }}>Loading attendance…</div>
       ) : error || noData ? (
         <div data-testid="attendance-empty" style={{ minHeight: 220, display: "grid", placeItems: "center", border: `1px dashed ${T.border}`, borderRadius: 10, color: "var(--v2-mid)", fontSize: T.fsBase }}>
           <span>No data for this scope</span>
           {attendance?.hasData === false && <span data-testid="attendance-efficiency-no-data" style={{ display: "none" }}>Seat utilization unavailable for this scope</span>}
         </div>
      ) : (
        <div
          className="attendance-columns"
          data-testid="attendance-columns"
          style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 0 }}
        >
          <div style={{ minWidth: 0, paddingRight: 16, borderRight: `1px solid ${T.hair}` }}>
             <div data-testid="attendance-headline-label" style={{ color: T.mid, fontSize: T.fsXs, fontWeight: T.fwBold, letterSpacing: ".06em", textTransform: "uppercase" }}>AVG ATTENDANCE</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 0, marginTop: 5 }}>
              <strong data-testid="attendance-headline" style={{ color: "var(--v2-dark)", fontSize: T.fsXl, fontWeight: T.fwDisplay }}>
                {attendanceMode === "count"
                  ? latestAttendanceCount == null ? "—" : latestAttendanceCount.toLocaleString("en-US")
                  : latest?.avgPct != null ? `${Math.round(latest.avgPct)}%` : "—"}
              </strong>
               {attendanceMode === "count" && (
                 <span data-testid="attendance-headline-unit" style={{ color: T.light, fontSize: T.fsXs, fontWeight: T.fwRegular, marginLeft: 7 }}>
                   people
                 </span>
               )}
               <span data-testid="attendance-headline-month" style={{ color: T.light, fontSize: T.fsXs, fontWeight: T.fwRegular, marginLeft: 4 }}>{cardPeriodLabel}</span>
            </div>
            <div data-testid="attendance-headline-basis" style={{ color: "var(--v2-mid)", fontSize: T.fsXs }}>
              {attendanceBasisSentence}
            </div>
            <div style={{ display: "flex", gap: 12, color: "var(--v2-mid)", fontSize: T.fsXs, marginBottom: 3 }}>
                 <span><i style={{ display: "inline-block", width: 15, borderTop: `2px solid ${T.accent}`, marginRight: 5, verticalAlign: "middle" }} />Avg attendance</span>
                 <span><i style={{ display: "inline-block", width: 15, borderTop: `2px dashed ${T.light}`, marginRight: 5, verticalAlign: "middle" }} />Peak attendance</span>
            </div>
            <div {...monthlyIntent.containerProps} data-testid="attendance-trend-chart" data-first-month={fmtAttendanceMonth(chartData[0]?.month)}>
              <ResponsiveContainer width="100%" height={150}>
                <AreaChart {...monthlyIntent.chartProps} data={chartData} margin={{ top: 8, right: 8, left: -22, bottom: 0 }}>
                <CartesianGrid stroke="var(--v2-hair)" />
                <XAxis dataKey="month" tickFormatter={fmtAttendanceMonth} interval={0} minTickGap={0} tick={{ fontSize: T.fsXs, fill: "var(--v2-light)" }} axisLine={false} tickLine={false} />
                <YAxis
                  domain={attendanceMode === "count" ? [0, "dataMax + dataMax * 0.1"] : [0, 100]}
                  tickFormatter={(value) => attendanceMode === "count" ? Math.round(value).toLocaleString("en-US") : `${value}%`}
                  tick={{ fontSize: T.fsXs, fill: "var(--v2-light)" }} axisLine={false} tickLine={false}
                />
                <Tooltip {...monthlyIntent.tooltipProps} content={<AttendanceTrendTooltip mode={attendanceMode} />} />
                <Area type="monotone" dataKey="avg" name={attendanceMode === "count" ? "Avg attendance (people)" : "Avg attendance (% of assigned)"} stroke={T.accent} fill="none" strokeWidth={2.5} connectNulls={false} />
                <Area type="monotone" dataKey="peak" name={attendanceMode === "count" ? "Peak attendance (people)" : "Peak attendance (% of assigned)"} stroke={T.light} fill="none" strokeWidth={2} strokeDasharray="5 4" connectNulls={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div style={{ minWidth: 0, padding: "0 16px", borderRight: `1px solid ${T.hair}` }}>
            <div data-testid="attendance-weekly-pattern-header" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
               <div style={{ color: T.mid, fontSize: T.fsXs, fontWeight: T.fwBold, letterSpacing: ".06em", textTransform: "uppercase" }}>WEEKLY PATTERN</div>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <button type="button" aria-label="Previous attendance week" onClick={() => stepWeek(-1)} disabled={!weeks.length} style={{ border: `1px solid ${T.border}`, background: T.white, color: "var(--v2-mid)", borderRadius: 6, width: 24, height: 24 }}>‹</button>
                <div ref={weekPickerRef} style={{ position: "relative" }}>
                  <button
                    type="button"
                    aria-label="Attendance week"
                    aria-haspopup="listbox"
                    aria-expanded={weekMenuOpen}
                    data-testid="attendance-week-select"
                    onClick={() => setWeekMenuOpen((isOpen) => !isOpen)}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6, maxWidth: 150,
                      border: `1px solid ${T.border}`, background: T.white, color: "var(--v2-dark)",
                      borderRadius: 8, padding: "4px 8px", fontSize: T.fsXs,
                      fontWeight: T.fwSemibold, cursor: "pointer", whiteSpace: "nowrap",
                    }}
                  >
                    <span>{selectedWeek?.weekLabel ?? "Typical week"}</span>
                    <span aria-hidden="true" style={{ color: "var(--v2-mid)", position: "relative", top: -2 }}>⌄</span>
                  </button>
                  {weekMenuOpen && (
                    <PickerPopover
                      testId="attendance-week-menu"
                      width="max-content"
                      maxHeight={320}
                      style={{
                        left: 0, right: "auto", minWidth: 150,
                        maxWidth: "calc(100vw - 24px)", boxSizing: "border-box",
                      }}
                    >
                      <PickerOption
                        selected={!selectedWeekStart}
                        testId="attendance-week-option-typical"
                        onClick={() => {
                          setSelectedWeekStart(null);
                          setWeekMenuOpen(false);
                        }}
                      >
                        Typical week
                      </PickerOption>
                      {weeks.slice().reverse().map((week) => (
                        <PickerOption
                          key={week.weekStart}
                          selected={selectedWeekStart === week.weekStart}
                          testId={`attendance-week-option-${week.weekStart}`}
                          onClick={() => {
                            setSelectedWeekStart(week.weekStart);
                            setWeekMenuOpen(false);
                          }}
                        >
                          {week.weekLabel}
                        </PickerOption>
                      ))}
                    </PickerPopover>
                  )}
                </div>
                <button type="button" aria-label="Next attendance week" onClick={() => stepWeek(1)} disabled={!weeks.length} style={{ border: `1px solid ${T.border}`, background: T.white, color: "var(--v2-mid)", borderRadius: 6, width: 24, height: 24 }}>›</button>
              </div>
            </div>
            <div data-testid="attendance-week-caption" style={{ color: "var(--v2-mid)", fontSize: T.fsXs, margin: "5px 0 8px" }}>
                {selectedWeek
                  ? `${(selectedWeek.weekLabel ?? `week of ${selectedWeek.weekStart ?? "unknown date"}`).replace(/^Week of /, "week of ")}${selectedWeek.weekStart && selectedWeek.weekEnd ? ` · ${formatAttendanceWeekRange(selectedWeek.weekStart, selectedWeek.weekEnd) ?? ""}` : ""}`
                  : `Typical week · ${attendancePeriodLabel}${displayedWeekRange ? ` · ${fmtAttendanceRange(displayedWeekRange)}` : ""}`}
            </div>
            <div {...weeklyIntent.containerProps} data-testid="attendance-week-chart">
            <ResponsiveContainer width="100%" height={150}>
              <BarChart {...weeklyIntent.chartProps} data={weekChartData} margin={{ top: 18, right: 4, left: -22, bottom: 0 }}
                onMouseLeave={weeklyIntent.reset}>
                <XAxis dataKey="label" tick={{ fontSize: T.fsXs, fill: "var(--v2-light)" }} axisLine={false} tickLine={false} />
                <YAxis
                  domain={attendanceMode === "count" ? [0, "dataMax + dataMax * 0.1"] : [0, 100]}
                  tickFormatter={(value) => attendanceMode === "count" ? Math.round(value).toLocaleString("en-US") : `${value}%`}
                  tick={{ fontSize: T.fsXs, fill: "var(--v2-light)" }} axisLine={false} tickLine={false}
                />
                <Tooltip {...weeklyIntent.tooltipProps} content={<AttendanceWeeklyTooltip mode={attendanceMode} />} />
                 <Bar
                   dataKey="valuePct"
                   name={attendanceMode === "count" ? "Attendance (people)" : "Attendance (% of assigned)"}
                   radius={[4, 4, 0, 0]}
                   label={{ position: "top", fontSize: T.fsXs, formatter: (value) => value == null ? "—" : attendanceMode === "count" ? Math.round(value).toLocaleString("en-US") : `${Math.round(value)}%` }}
                 >
                   {(weekData?.days ?? []).map((day, index) => <Cell key={day.day} fill={values[index] != null && values[index] === peak ? T.accent : T.blueSoftBorder} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            </div>
             <div style={{ color: "var(--v2-mid)", fontSize: T.fsXs }}>
               {peakDayLabel ? `Peak attendance: ${peakDayLabel}` : "Peak attendance: unavailable"}
             </div>
          </div>
          <div style={{ minWidth: 0, paddingLeft: 16 }}>
             <div style={{ color: T.mid, fontSize: T.fsXs, fontWeight: T.fwBold, letterSpacing: ".06em", textTransform: "uppercase" }}>SEAT UTILIZATION</div>
            {efficiencyNoData ? (
               <>
                 <div data-testid="attendance-efficiency-scope" style={{ color: "var(--v2-mid)", fontSize: T.fsXs, marginTop: 2 }}>{efficiencyScopeLabel} · {efficiencyMonthLabel}</div>
                 <div data-testid="attendance-efficiency-no-data" style={{ minHeight: 150, display: "grid", placeItems: "center", color: "var(--v2-mid)", fontSize: T.fsBase }}>
                   Seat utilization unavailable for this scope
                 </div>
               </>
            ) : (
              <>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 0, marginTop: 5 }}>
                  <strong data-testid="attendance-efficiency-headline" style={{ color: T.attendanceUsed, fontSize: T.fsXl, fontWeight: T.fwDisplay }}>
                    {attendanceMode === "count"
                       ? averageSeatsUsed?.toLocaleString("en-US") ?? "—"
                      : `${averageSeatPct}%`}
                  </strong>
                   <span data-testid="attendance-efficiency-unit" style={{ color: T.light, fontSize: T.fsXs, fontWeight: T.fwRegular, marginLeft: 7 }}>
                     {attendanceMode === "count"
                       ? `of ${Number(totalSeats).toLocaleString("en-US")} seats`
                       : "of seats"}
                   </span>
                   <span data-testid="attendance-efficiency-month" style={{ color: T.light, fontSize: T.fsXs, fontWeight: T.fwRegular, marginLeft: 4 }}>{cardPeriodLabel}</span>
                 </div>
                  <div data-testid="attendance-efficiency-basis" style={{ color: "var(--v2-mid)", fontSize: T.fsXs }}>
                    {utilizationBasisSentence}
                  </div>
                  {attendanceMode === "percent" && peakDayAttended > totalSeats && (
                    <div data-testid="attendance-efficiency-over-capacity" style={{ color: T.amber, fontSize: T.fsXs, marginTop: 3 }}>
                      Peak presence was {peakSeatsUsed?.toLocaleString("en-US")} people — {countValue(peakDayAttended - totalSeats)?.toLocaleString("en-US")} above {Number(totalSeats).toLocaleString("en-US")} installed seats.
                    </div>
                  )}
                <div
                  data-testid="attendance-efficiency-bar"
                    aria-label={attendanceMode === "count"
                      ? `Installed-seat utilization at reporting offices: ${averageSeatsUsed?.toLocaleString("en-US") ?? "—"} people present on an average day, ${peakSeatsUsed?.toLocaleString("en-US") ?? "—"} people at the pooled peak; ${neverUsedSeatsDisplay?.toLocaleString("en-US") ?? "—"} seats never used`
                       : `Seat utilization of installed seats at reporting offices: ${averageSeatPct}% on an average day, ${peakSeatPct}% at the pooled peak; ${neverUsedPct}% of installed seats never used${peakDayAttended > totalSeats ? `; pooled peak was ${peakSeatsUsed} people, ${countValue(peakDayAttended - totalSeats)} above installed seats` : ""}`}
                  style={{ height: 24, display: "flex", overflow: "hidden", borderRadius: 7, background: T.lightGray, margin: "14px 0 0" }}
                >
                  <span data-testid="attendance-efficiency-used-avg" style={{ width: `${displayAverageShare * 100}%`, background: T.attendanceUsed }} />
                  <span data-testid="attendance-efficiency-peak-swing" style={{ width: `${peakSwingShare * 100}%`, background: T.attendancePeakSwing }} />
                  <span data-testid="attendance-efficiency-never-used" style={{ width: `${displayNeverUsedShare * 100}%`, background: T.attendanceNeverUsed }} />
                </div>
                 <div data-testid="attendance-efficiency-ticks" style={{ display: "flex", justifyContent: "space-between", marginTop: 5, fontSize: T.fsXs, color: "var(--v2-light)" }}>
                   <span>0</span>
                   <span>{Number(totalSeats).toLocaleString()}</span>
                </div>
                <div data-testid="attendance-efficiency-legend" style={{ display: "flex", gap: 10, flexWrap: "wrap", color: "var(--v2-mid)", fontSize: T.fsXs, marginTop: 8 }}>
                   <span data-testid="attendance-efficiency-legend-used"><i style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2, background: T.attendanceUsed, marginRight: 5, verticalAlign: "middle" }} />Used, avg day <strong>{efficiencyLegendValues[0]}</strong></span>
                   <span data-testid="attendance-efficiency-legend-peak"><i style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2, background: T.attendancePeakSwing, marginRight: 5, verticalAlign: "middle" }} />Peak swing <strong>{efficiencyLegendValues[1]}</strong></span>
                   <span data-testid="attendance-efficiency-legend-never"><i style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2, background: T.attendanceNeverUsed, marginRight: 5, verticalAlign: "middle" }} />Never used <strong>{efficiencyLegendValues[2]}</strong></span>
                </div>
                   <div data-testid="attendance-efficiency" style={{ color: T.red, fontSize: T.fsXs, fontWeight: T.fwRegular, lineHeight: 1.5, marginTop: 12 }}>
                      <strong data-testid="attendance-efficiency-waste" style={{ color: T.red, fontWeight: T.fwRegular }}>
                       {attendanceMode === "count"
                      ? `${neverUsedSeatsDisplay?.toLocaleString("en-US") ?? "—"} seats sit unused even at peak`
                       : `${neverUsedPct}% of seats (~${Math.round(neverUsedSeats).toLocaleString()}) sit unused even at peak`}
                     </strong>
                   {annualPortfolioRent != null && (
                      <> · <strong style={{ color: T.red, fontWeight: T.fwRegular }}>≈{fmtAttendanceCost(neverUsedRent)}/yr</strong> in rent on seats no one uses.</>
                   )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </Card>
    </div>
  );
}

/* ─── Glance strip formatters ────────────────────────────────────────────── */

/** Format sq ft for glance tile: ≥1M → "X.XM", else comma-separated integer. */
function fmtGlanceSqft(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  return Math.round(n).toLocaleString();
}

function HomeBand({ label, testId, first = false, hideChrome = false, children }) {
  return (
    <section data-testid={testId} {...(hideChrome ? {} : { "aria-label": label })}>
      {hideChrome ? null : (
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        margin: first ? "0 0 8px" : "18px 0 8px",
      }}>
        <span style={{
          fontSize: T.fsXs,
          fontWeight: T.fwBold,
          letterSpacing: ".1em",
          lineHeight: 1.2,
          textTransform: "uppercase",
          color: T.mid,
          whiteSpace: "nowrap",
        }}>
          {label}
        </span>
        <span aria-hidden="true" style={{ height: 1, flex: 1, background: "var(--v2-line)" }} />
      </div>
      )}
      {children}
    </section>
  );
}

/* ─── Root export ────────────────────────────────────────────────────────── */

export function PulsePage({
  onNavigate, currentRole = "viewer", onStartWorkflow, onOpenLinkedRun,
  focusDecisionLocationId = null, onDecisionFocusHandled,
  pulseView = null,
} = {}) {
  const navigate       = useNavigate();

  // PULSE-DRAWER-HISTORY-STATE: Single source of truth for the intended peek ID.
  // Seeded from the URL at mount; kept current by handleCardClick, handleCloseDrawer,
  // and the popstate listener so the sync effect always resolves the right card.
  // Per project memory: useSearchParams() is forbidden (stalls React 18 transitions).
  const intendedPeekRef = useRef(
    new URLSearchParams(window.location.search).get("peek") ?? null
  );

  const featureContext = useFeatureFlagContext();
  const { flags: featureFlags, flagsReady } = featureContext;
  const canSeePlan = homeNavEnabled(featureContext, "plan");
  const canSeeReports = homeNavEnabled(featureContext, "reports");
  const canSeeLeases = homeNavEnabled(featureContext, "leases");
  const canSeeOffices = homeNavEnabled(featureContext, "portfolio");
  const canSeeProjects = homeNavEnabled(featureContext, "projects");
  const canSeeReviews = homeNavEnabled(featureContext, "reviews");
  const canSeeRadar = homeNavEnabled(featureContext, "radar");
  const canSeeWorkflows = homeNavEnabled(featureContext, "workflows");
  const sensitivity = useSensitivityPolicy();
  const canSeeAnnualCost = sensitivity.canSee("home.annual_portfolio_cost");
  const canSeeCostCommitments = canSeeLeases && sensitivity.canSee("home.cost_commitments_band");
  const inFocusLayout  = featureFlags[IN_FOCUS_LAYOUT] ?? 'classic';
  const showInFocus    = inFocusLayout !== 'off';
  const scoringArchived = !!featureFlags[FEATURE_SCORING_MODEL_ARCHIVED];
  const isEditor = currentRole === "admin" || currentRole === "editor";
  const [nextDecisions,    setNextDecisions]    = useState(null);
  const [planMarkets,      setPlanMarkets]      = useState([]);
  const [planMarketsLoaded, setPlanMarketsLoaded] = useState(false);
  const [planMarketsDegraded, setPlanMarketsDegraded] = useState(false);
  const [motionProjects, setMotionProjects] = useState([]);
  const [motionMilestones, setMotionMilestones] = useState([]);
  const [motionDataLoading, setMotionDataLoading] = useState(false);
  const [projectsDegraded, setProjectsDegraded] = useState(false);
  const [milestonesDegraded, setMilestonesDegraded] = useState(false);
  const [locations,        setLocations]        = useState([]);
  const [locationsLoaded,  setLocationsLoaded]  = useState(false);
  const [homeAttendance,   setHomeAttendance]   = useState(null);
  const [attendanceLoaded, setAttendanceLoaded] = useState(false);
  const [attendanceError,  setAttendanceError]  = useState(false);
  const [locationRisk,     setLocationRisk]     = useState(null);
  const [categoryImages,   setCategoryImages]   = useState({});
  const [drawerCard,       setDrawerCard]       = useState(null);
  const [derivedLeases,    setDerivedLeases]    = useState([]);
  const [infocusLoading,   setInfocusLoading]   = useState(true);
  const [snoozePickerOpen, setSnoozePickerOpen] = useState(false);
  const [annualRent, setAnnualRent] = useState(null);
  const [annualRentLoaded, setAnnualRentLoaded] = useState(false);
  const fxBasisSummary = formatFxBasisSummary(annualRent?.fx?.basis, annualRent?.fx?.liveFetchedAt);
  const [headcountTrendData, setHeadcountTrendData] = useState(null);

  const portfolioMotionModel = useMemo(() => buildPortfolioMotionModel({
    projects: motionProjects,
    milestones: motionMilestones,
    markets: planMarkets,
    milestonesDegraded,
    planDegraded: planMarketsDegraded,
    projectsDegraded,
  }), [
    motionProjects, motionMilestones, planMarkets,
    milestonesDegraded, planMarketsDegraded, projectsDegraded,
  ]);

  useEffect(() => {
    if (!canSeeAnnualCost) {
      setAnnualRent(null);
      setAnnualRentLoaded(true);
      return;
    }
    let disposed = false;
    let request = 0;
    const load = () => {
      const currentRequest = ++request;
      setAnnualRent(null);
      setAnnualRentLoaded(false);
      // Commit the rent and its provenance together. Never show a new FX label
      // beside a prior basis's cached dollars, or retain dollars after a failure.
      Promise.all([
        Promise.resolve().then(() => getPortfolioLeases()),
        Promise.resolve().then(() => getFxBasisTransparency()),
      ])
        .then(([rows, fx]) => {
          if (!disposed && currentRequest === request) {
            setAnnualRent(Array.isArray(rows) && fx?.basis
              ? { ...deriveAnnualRentKpi(selectDefaultPortfolioLeases(rows)), fx }
              : null);
          }
        })
        .catch(() => { if (!disposed && currentRequest === request) setAnnualRent(null); })
        .finally(() => { if (!disposed && currentRequest === request) setAnnualRentLoaded(true); });
    };
    load();
    window.addEventListener("ripple:fx-rates-invalidated", load);
    return () => {
      disposed = true;
      window.removeEventListener("ripple:fx-rates-invalidated", load);
    };
  }, [canSeeAnnualCost]);

  // Reset snooze picker whenever drawer is closed or card changes.
  useEffect(() => { if (!drawerCard) setSnoozePickerOpen(false); }, [drawerCard]);

  // Fetch locations for glance strip stats.
  useEffect(() => {
    getLocations()
      .then((list) => setLocations(Array.isArray(list) ? list : []))
      .catch(() => setLocations([]))
      .finally(() => setLocationsLoaded(true));
  }, []);

  // The all-location Attendance response is shared by the Home glance tile
  // and the pane's default scope. Scoped picker requests stay local to the
  // pane, so changing the picker cannot make the Home tile drift.
  useEffect(() => {
    let disposed = false;
    setAttendanceLoaded(false);
    setAttendanceError(false);
    getPulseAttendance()
      .then((result) => { if (!disposed) setHomeAttendance(result ?? null); })
      .catch(() => {
        if (!disposed) {
          setHomeAttendance(null);
          setAttendanceError(true);
        }
      })
      .finally(() => { if (!disposed) setAttendanceLoaded(true); });
    return () => { disposed = true; };
  }, []);

  // Keep the mounted Home page in sync with location and capacity-schedule
  // writes. /api/plan is intentionally uncached, so each invalidation can
  // safely fetch the current portfolio rather than trying to patch a derived
  // market aggregate locally.
  useEffect(() => {
    if (!flagsReady) {
      setPlanMarkets([]);
      setPlanMarketsLoaded(false);
      setPlanMarketsDegraded(false);
      return undefined;
    }
    let disposed = false;
    setPlanMarketsLoaded(false);
    setPlanMarketsDegraded(false);
    let requestVersion = 0;
    let inFlight = false;
    let refreshQueued = false;

    async function refreshPlan() {
      if (inFlight) {
        refreshQueued = true;
        return;
      }
      inFlight = true;
      const version = ++requestVersion;
      try {
        const result = await getPlan();
        if (disposed || version !== requestVersion) return;
        const valid = Array.isArray(result?.markets);
        setPlanMarkets(valid ? result.markets : []);
        setPlanMarketsDegraded(!valid);
        setPlanMarketsLoaded(true);
      } catch (err) {
        if (disposed || version !== requestVersion) return;
        console.warn("[PulsePage] Plan fetch failed; showing no pipeline data", err);
        setPlanMarkets([]);
        setPlanMarketsDegraded(true);
        setPlanMarketsLoaded(true);
      } finally {
        inFlight = false;
        if (!disposed && refreshQueued) {
          refreshQueued = false;
          void refreshPlan();
        }
      }
    }

    const handleInvalidation = () => { void refreshPlan(); };
    subscribePlanInvalidation(handleInvalidation);
    void refreshPlan();
    return () => {
      disposed = true;
      unsubscribePlanInvalidation(handleInvalidation);
    };
  }, [flagsReady]);

  // Projects is governed by the same left-navigation state as its Admin
  // toggle. Avoid fetching project data while the Home summary is disabled;
  // enabling it in this mounted page starts a fresh read-only load.
  useEffect(() => {
    if (!flagsReady || !canSeeProjects) {
      setMotionProjects([]);
      setMotionMilestones([]);
      setProjectsDegraded(false);
      setMilestonesDegraded(false);
      setMotionDataLoading(false);
      return undefined;
    }

    let disposed = false;
    setMotionDataLoading(true);
    Promise.allSettled([
      Promise.resolve().then(() => getProjects()),
      Promise.resolve().then(() => getAllNextMilestones()),
    ])
      .then(([projectsResult, milestonesResult]) => {
        if (disposed) return;
        const validProjects = projectsResult.status === "fulfilled" && Array.isArray(projectsResult.value);
        const validMilestones = milestonesResult.status === "fulfilled" && Array.isArray(milestonesResult.value);
        setMotionProjects(validProjects ? projectsResult.value : []);
        setMotionMilestones(validMilestones ? milestonesResult.value : []);
        setProjectsDegraded(!validProjects);
        setMilestonesDegraded(!validMilestones);
      })
      .finally(() => {
        if (!disposed) setMotionDataLoading(false);
      });

    return () => { disposed = true; };
  }, [flagsReady, canSeeProjects]);

  // ── Glance strip stats ───────────────────────────────────────────────────────
  // Derived from already-loaded locations + the canonical
  // all-location attendance response. Shows "…" while any source is loading.
  // Shows "—" when a stat has no data.
  const glanceStats = useMemo(() => {
    // Show "…" only while fetches are genuinely in flight.
    // Once both settle (success or error), resolve to computed values or "—".
    if (!locationsLoaded || !planMarketsLoaded || !attendanceLoaded) return null;
    const today = dateOnly(new Date());

    const operationalLocations = locations.filter(
      (location) => USABLE_CAPACITY_STAGES_SET.has(location.stage) && location.disposition !== "exit",
    );
    const offices = operationalLocations.length;
    // Keep the descriptor aligned with the same operational/dormant offices
    // counted above. Plan copy describing seats across offices is separate.
    const activeMarkets = getDistinctMarketCount(operationalLocations);

    const totalSqft = operationalLocations.reduce(
      (sum, l) => sum + (l.exitDate && dateOnly(l.exitDate) <= today
        && (!l.reopenDate || dateOnly(l.reopenDate) > today)
        ? 0 : (getDisplayedSqft(l, null) ?? 0)),
      0,
    );

    const totalSeats = operationalLocations.reduce(
      (sum, l) => sum + (Number(l.installedCapacity) || 0),
      0,
    );

    const assignedHc = locations.reduce((sum, l) => sum + (Number(l.latestHeadcount?.totalCount) || 0), 0);

    const latestAttendance = homeAttendance?.latestCompleteMonth ?? null;
    const avgAttendance = latestAttendance?.avgPct ?? null;
    const statusLabel = formatReportingPeriodStatus(
      homeAttendance?.reportingPeriod,
      latestAttendance?.monthDate,
      { emptyShowsTarget: true, longMonth: true },
    );
    const attendanceMonth = statusLabel
      ? statusLabel.startsWith("As of ") ? statusLabel : `As of ${statusLabel}`
      : latestAttendance?.monthLabel ? `As of ${latestAttendance.monthLabel}` : null;
    const totalAttendanceSeats = Number(homeAttendance?.totalSeats);
    const rawAverageDayAttended = homeAttendance?.averageDayAttended;
    const averageDayAttended = rawAverageDayAttended == null ? null : Number(rawAverageDayAttended);
    const rawPeakDayAttended = homeAttendance?.peakDayAttended;
    const peakDayAttended = rawPeakDayAttended == null ? null : Number(rawPeakDayAttended);
    const hasSeatUtilization = homeAttendance?.hasData === true
      && Number.isFinite(totalAttendanceSeats) && totalAttendanceSeats > 0
      && Number.isFinite(averageDayAttended) && averageDayAttended >= 0
      && Number.isFinite(peakDayAttended) && peakDayAttended >= 0;
    const seatUtilizationAverage = hasSeatUtilization
      ? Math.round(Math.min(1, Math.max(0, averageDayAttended / totalAttendanceSeats)) * 100)
      : null;
    const seatUtilizationPeak = hasSeatUtilization
      ? Math.round(Math.min(1, Math.max(0, peakDayAttended / totalAttendanceSeats)) * 100)
      : null;
    const attendanceCoverage = attendanceCoverageLine(
      homeAttendance?.coverage?.reportingOfficeCount
        ?? homeAttendance?.reportingOfficeCount
        ?? homeAttendance?.reportingPeriod?.officesReported,
      homeAttendance?.coverage?.scopeOfficeCount
        ?? homeAttendance?.scopeOfficeCount
        ?? homeAttendance?.reportingPeriod?.totalOffices,
    );
    const attendancePeriod = attendanceMonth ?? "Reporting period unavailable";
    const reportingMonth = formatReportingMonth(homeAttendance?.reportingPeriod?.month, { long: true });
    const reportingMonthKeyValue = reportingMonthKey(homeAttendance?.reportingPeriod?.month);
    const attendanceDataMonthKey = reportingMonthKey(latestAttendance?.monthDate);
    const attendanceDataMonth = formatReportingMonth(latestAttendance?.monthDate, { long: true });
    const distributedRows = (headcountTrendData?.aggregate ?? [])
      .map((row) => ({ row, month: reportingMonthKey(row?.snapshotDate) }))
      .filter(({ month }) => month && (!reportingMonthKeyValue || month <= reportingMonthKeyValue))
      .sort((a, b) => a.month.localeCompare(b.month));
    const distributedValue = distributedRows.at(-1)?.row?.unassigned;
    const distributedHeadcount = distributedValue == null || !Number.isFinite(Number(distributedValue))
      ? null
      : Number(distributedValue);
    const targetCoverageRatio = homeAttendance?.reportingPeriod?.coverageRatio != null
      ? Number(homeAttendance.reportingPeriod.coverageRatio)
      : Number(homeAttendance?.reportingPeriod?.eligibleOfficeDays) > 0
        ? Number(homeAttendance.reportingPeriod.observedOfficeDays ?? 0)
          / Number(homeAttendance.reportingPeriod.eligibleOfficeDays)
        : NaN;
    const expectedAttendanceDays = Number(latestAttendance?.expectedDays);
    const attendanceCoverageRatio = Number.isFinite(expectedAttendanceDays) && expectedAttendanceDays > 0
      ? Number(latestAttendance.coveredDays ?? 0) / expectedAttendanceDays
      : (!attendanceDataMonthKey || attendanceDataMonthKey === reportingMonthKeyValue)
        ? targetCoverageRatio
        : NaN;
    const attendanceCoveragePercent = Number.isFinite(attendanceCoverageRatio)
      ? `${Math.round(attendanceCoverageRatio * 100)}%`
      : "—";
    const attendanceCoveragePeriod = attendanceDataMonthKey
      && reportingMonthKeyValue
      && attendanceDataMonthKey < reportingMonthKeyValue
      ? `${attendanceDataMonth ?? "Selected month"} coverage: `
      : "";
    const densityPerSeat = totalSeats > 0 ? Math.round(totalSqft / totalSeats) : null;

    return {
      offices, activeMarkets, totalSqft, totalSeats, assignedHc, avgAttendance, attendanceMonth,
      seatUtilizationAverage, seatUtilizationPeak, attendanceCoverage, attendancePeriod,
      reportingMonth, distributedHeadcount, attendanceCoveragePercent, attendanceCoveragePeriod, densityPerSeat,
    };
  }, [attendanceLoaded, headcountTrendData, homeAttendance, locations, locationsLoaded, planMarketsLoaded]);

  // PULSE-DRAWER-HISTORY-STATE: Ref so the mount-registered popstate handler
  // always reads the latest card list without re-registering.
  const locationRiskRef = useRef(null);
  useEffect(() => { locationRiskRef.current = locationRisk; }, [locationRisk]);

  // PULSE-DRAWER-HISTORY-STATE: Listen for browser Back/Forward while mounted.
  // Navigating between /pulse and /pulse?peek=X is a same-route transition —
  // React Router keeps PulsePage mounted so the initial ref would be stale.
  // Strategy: always update intendedPeekRef; if cards are already available
  // sync the drawer immediately, otherwise the sync effect below resolves it
  // when loading finishes.
  useEffect(() => {
    function onPopState() {
      const peekId = new URLSearchParams(window.location.search).get("peek") ?? null;
      intendedPeekRef.current = peekId;
      // Cards not yet available — the sync-on-load effect will resolve later.
      if (locationRiskRef.current === null) return;
      const cards = locationRiskRef.current.cards ?? [];
      const match = peekId ? cards.find((c) => (c.locationId ?? c.id) === peekId) : null;
      setDrawerCard(match ?? null);
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Drawer open / close with URL history ────────────────────────────────────

  // PULSE-DRAWER-HISTORY-STATE: Close drawer — clear only ?peek, preserve other
  // params (e.g. ?view=datatrust). Replace so no extra history entry is created.
  function handleCloseDrawer() {
    intendedPeekRef.current = null;
    setDrawerCard(null);
    const p = new URLSearchParams(window.location.search);
    p.delete("peek");
    const qs = p.toString();
    navigate(qs ? "?" + qs : window.location.pathname, { replace: true });
  }

  // PULSE-DRAWER-HISTORY-STATE: Open drawer — set ?peek=<id> while preserving
  // all other existing query params (e.g. ?view=datatrust).
  // Push when opening fresh (so Back returns with the drawer open).
  // Replace when switching between cards (avoids stacking entries).
  function handleCardClick(card) {
    const peekKey = card?.locationId ?? card?.id ?? null;
    intendedPeekRef.current = peekKey;
    setDrawerCard(card);
    if (!peekKey) return;
    const p = new URLSearchParams(window.location.search);
    const currentPeek = p.get("peek");
    p.set("peek", peekKey);
    const qs = p.toString();
    if (currentPeek) {
      // Switching cards — replace so we don't stack duplicate history entries.
      navigate("?" + qs, { replace: true });
    } else {
      // Opening fresh — push so Back returns to this state with the drawer open.
      navigate("?" + qs);
    }
  }

  // ── Dismiss / Snooze / Restore handlers ─────────────────────────────────────
  // All three update locationRisk optimistically so the UI responds instantly,
  // then fire the API call and rollback on failure.

  function handleAcknowledge(card) {
    if (!isEditor || !card?.cardKey) return;
    const dismissed = { ...card, dismissedAction: "acknowledged", snoozedUntil: null };
    setLocationRisk(prev => prev ? {
      ...prev,
      cards:          prev.cards.filter(c => c.cardKey !== card.cardKey),
      dismissedCards: [...(prev.dismissedCards ?? []), dismissed],
    } : prev);
    handleCloseDrawer();
    acknowledgeInFocusCard(card.cardKey).catch(() => {
      setLocationRisk(prev => prev ? {
        ...prev,
        cards:          [...prev.cards, card],
        dismissedCards: (prev.dismissedCards ?? []).filter(c => c.cardKey !== card.cardKey),
      } : prev);
    });
  }

  function handleSnooze(card, days) {
    if (!isEditor || !card?.cardKey) return;
    const d = new Date();
    d.setDate(d.getDate() + days);
    const snoozeUntil = d.toISOString().slice(0, 10);
    const dismissed   = { ...card, dismissedAction: "snoozed", snoozedUntil: snoozeUntil };
    setLocationRisk(prev => prev ? {
      ...prev,
      cards:          prev.cards.filter(c => c.cardKey !== card.cardKey),
      dismissedCards: [...(prev.dismissedCards ?? []), dismissed],
    } : prev);
    setSnoozePickerOpen(false);
    handleCloseDrawer();
    snoozeInFocusCard(card.cardKey, snoozeUntil).catch(() => {
      setLocationRisk(prev => prev ? {
        ...prev,
        cards:          [...prev.cards, card],
        dismissedCards: (prev.dismissedCards ?? []).filter(c => c.cardKey !== card.cardKey),
      } : prev);
    });
  }

  function handleRestore(cardKey) {
    if (!isEditor) return;
    const card = locationRisk?.dismissedCards?.find(c => c.cardKey === cardKey);
    if (!card) return;
    // eslint-disable-next-line no-unused-vars
    const { dismissedAction, snoozedUntil, ...cleanCard } = card;
    setLocationRisk(prev => prev ? {
      ...prev,
      cards:          [...prev.cards, cleanCard],
      dismissedCards: prev.dismissedCards.filter(c => c.cardKey !== cardKey),
    } : prev);
    restoreInFocusCard(cardKey).catch(() => {
      setLocationRisk(prev => prev ? {
        ...prev,
        cards:          prev.cards.filter(c => c.cardKey !== cardKey),
        dismissedCards: [...(prev.dismissedCards ?? []), card],
      } : prev);
    });
  }
  useEffect(() => {
    if (!flagsReady) return undefined;
    let disposed = false;
    if (!canSeePlan) {
      setLocationRisk((previous) => {
        if (!previous) return previous;
        const withoutPressure = (cards) => (cards ?? []).filter(
          (card) => card?.id !== "insight-pressure" && card?.cardKey !== "insight-pressure:Insight",
        );
        return {
          ...previous,
          cards: withoutPressure(previous.cards),
          dismissedCards: withoutPressure(previous.dismissedCards),
        };
      });
      if (
        drawerCard?.id === "insight-pressure"
        || drawerCard?.cardKey === "insight-pressure:Insight"
        || intendedPeekRef.current === "insight-pressure"
      ) {
        handleCloseDrawer();
      }
    }
    const today = new Date();
    Promise.allSettled([
      getPulseNextDecisions(),
      getPulseNeedsAttention(),
      canSeeCostCommitments ? getCriticalDates() : Promise.resolve([]),
      canSeeCostCommitments ? getPortfolioLeases() : Promise.resolve([]),
      getPulseLocationRisk(),
      // Open Roles pressure is calculated against Markets capacity; Reports
      // access alone does not authorize that Home insight.
      canSeePlan && canSeeReports ? getOpenRolesReport() : Promise.resolve(null),
      canSeeReports ? getHeadcountTrendReport() : Promise.resolve(null),
      getPulseCategoryImages(),
    ]).then(([d, , cd, pl, lr, or_, hc, ci]) => {
      if (disposed) return;
      setHeadcountTrendData(hc.status === "fulfilled" ? hc.value : null);
      if (d.status  === "fulfilled") setNextDecisions(d.value?.nextDecisions ?? null);
      const criticalDates = (cd.status === "fulfilled" && Array.isArray(cd.value)) ? cd.value : [];
      const portfolioLeases = (pl.status === "fulfilled" && Array.isArray(pl.value)) ? pl.value : [];
      // Annual net headlines are confined to Leases/Plan, not Pulse's exposure.
      setDerivedLeases(deriveLeaseActions(portfolioLeases.map((lease) => ({
        ...lease,
        annualRentUsd: lease.contractualAnnualRentUsd !== undefined
          ? lease.contractualAnnualRentUsd : lease.annualRentUsd,
      })), criticalDates, today, Infinity));

      // Resolve category stock images (used for client-created Insight cards below).
      const catImages = (ci.status === "fulfilled" && ci.value && typeof ci.value === "object")
        ? ci.value
        : {};
      setCategoryImages(catImages);

      // Build insight cards from report data and merge them into the In-focus card feed.
      const insightCards = [];
      if (or_.status === "rejected") {
        console.warn("[PulsePage] Open Roles fetch failed; skipping pressure insight", or_.reason);
      }
      if (canSeePlan && or_.status === "fulfilled" && or_.value) {
        const orData = or_.value;
        const orInsight = computeOpenRolesInsight(orData.markets ?? [], orData.kpis ?? {});
        if (orInsight) {
          insightCards.push({
            id:       "insight-pressure",
            cardKey:  "insight-pressure:Insight",
            cat:      "Insight",
            nm:       orInsight.headline,
            sm:       `${orInsight.soWhat} · from Open Roles`,
            deepLink: orInsight.deepLink,
            action:   orInsight.action,
            urgency:  50,
            band:     "blue",
            due:      null,
            dueState: "none",
            imageUrl: catImages["Insight"] ?? null,
          });
        }
      }
      if (hc.status === "rejected") {
        console.warn("[PulsePage] Headcount Trend fetch failed; skipping growth insight", hc.reason);
      }
      if (hc.status === "fulfilled" && hc.value) {
        const hcData    = hc.value;
        const hcOffices = hcData.offices ?? [];
        const hcAgg     = hcData.aggregate ?? [];
        // Build snapByLocMonth for the rule function.
        const snapByLocMonth = new Map();
        for (const office of hcOffices) {
          for (const snap of (office.snapshots ?? [])) {
            const ym = snap.snapshotDate ? snap.snapshotDate.slice(0, 7) : null;
            if (ym) snapByLocMonth.set(`${office.locationId}|${ym}`, snap);
          }
        }
        // Build allMonths from aggregate.
        const hcAllMonths = hcAgg.length
          ? (() => {
              const firstDate  = hcAgg[0].snapshotDate;
              const latestDate = hcAgg[hcAgg.length - 1].snapshotDate;
              const startYear  = parseInt(firstDate.slice(0, 4), 10);
              const endYear    = parseInt(latestDate.slice(0, 4), 10);
              const endMonth   = parseInt(latestDate.slice(5, 7), 10);
              const months = [];
              for (let y = startYear; y <= endYear; y++) {
                const mEnd = y === endYear ? endMonth : 12;
                for (let m = 1; m <= mEnd; m++) {
                  months.push(`${y}-${String(m).padStart(2, "0")}`);
                }
              }
              return months;
            })()
          : [];
        const hcInsight = computeHeadcountGrowthInsight(hcOffices, snapByLocMonth, hcAllMonths);
        if (hcInsight) {
          insightCards.push({
            id:       "insight-growth",
            cardKey:  "insight-growth:Insight",
            cat:      "Insight",
            nm:       hcInsight.headline,
            sm:       `${hcInsight.soWhat} · from Headcount Trend`,
            deepLink: hcInsight.deepLink,
            action:   hcInsight.action,
            urgency:  50,
            band:     "blue",
            due:      null,
            dueState: "none",
            imageUrl: catImages["Insight"] ?? null,
          });
        }
      }

      // Merge insight cards into the location-risk card array.
      // When scoring is archived or the fetch failed, place insight cards alone
      // so they still surface in the In-focus feed.
      let riskData = null;
      if (lr.status === "fulfilled") {
        riskData = adaptLocationRisk(lr.value);
      }
      if (insightCards.length > 0) {
        const existingCards    = riskData?.cards          ?? [];
        const existingDismissed = riskData?.dismissedCards ?? [];
        setLocationRisk({ cards: [...existingCards, ...insightCards], dismissedCards: existingDismissed });
      } else {
        setLocationRisk(riskData);
      }

      setInfocusLoading(false);
    });
    return () => { disposed = true; };
  }, [flagsReady, canSeeCostCommitments, canSeePlan, canSeeReports]); // eslint-disable-line react-hooks/exhaustive-deps

  // WORKFLOWS-SLICE-2-LAUNCH-FROM-OBJECT: when arriving via a run-detail
  // "Started from <decision>" deep link, auto-open that card's drawer once
  // In Focus cards have loaded. No-ops gracefully when the In Focus panel
  // is hidden via the admin toggle — calls onDecisionFocusHandled so the
  // caller knows the param was consumed, but does not open a drawer.
  useEffect(() => {
    if (!focusDecisionLocationId || infocusLoading) return;
    if (showInFocus) {
      const cards = locationRisk?.cards ?? [];
      const match = cards.find((c) => c.locationId === focusDecisionLocationId);
      if (match) setDrawerCard(match);
    }
    onDecisionFocusHandled?.();
  }, [focusDecisionLocationId, infocusLoading, locationRisk, onDecisionFocusHandled, showInFocus]);

  // PULSE-DRAWER-HISTORY-STATE: Sync drawer with the intended peek ID whenever
  // loading settles or cards change. This handles three cases in one effect:
  //   1. Initial deep-link: intendedPeekRef was set from the mount URL.
  //   2. popstate-before-load: popstate updated the ref; this runs on load.
  //   3. popstate-after-load: popstate syncs directly; this is a harmless no-op
  //      (setDrawerCard called with the same value it already holds).
  // Does NOT run while loading to avoid a flash of "no drawer" on deep links.
  useEffect(() => {
    if (infocusLoading) return;
    const peekId = intendedPeekRef.current;
    // Only sync URL-driven peek state; when there is no intended peek ID, leave
    // the drawer alone so prop-driven opens (focusDecisionLocationId) are not clobbered.
    if (!peekId) return;
    const cards  = locationRisk?.cards ?? [];
    const match  = cards.find((c) => (c.locationId ?? c.id) === peekId);
    setDrawerCard(match ?? null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infocusLoading, locationRisk]);

  // ─── Portfolio Command views ─────────────────────────────────────────────────
  // datatrust | plan-coverage | review-cadence | portfolio-command all render
  // the PortfolioCommandPage shell with the appropriate active tab.
  const PORTFOLIO_COMMAND_VIEWS = new Set([
    "datatrust", "plan-coverage", "review-cadence", "portfolio-command",
  ]);
  const canOpenCommandView = pulseView !== "plan-coverage" || canSeePlan;
  if (pulseView && PORTFOLIO_COMMAND_VIEWS.has(pulseView) && canOpenCommandView) {
    // Default tab: portfolio-command resolves to datatrust.
    const activeTab = pulseView === "portfolio-command" ? "datatrust" : pulseView;
    return (
      <div
        data-testid="v2-pulse-page"
        style={{ maxWidth: "1400px", padding: "0 32px 40px", margin: "0 auto", width: "100%" }}
      >
        <PortfolioCommandPage
          activeTab={activeTab}
          onNavigate={onNavigate}
          userRole={currentRole}
          onOpenLinkedRun={onOpenLinkedRun}
          planEnabled={canSeePlan}
          reviewsEnabled
        />
      </div>
    );
  }

  return (
    <div data-testid="v2-pulse-page" style={{ maxWidth: "1400px", padding: "0 32px 40px", margin: "0 auto", width: "100%" }}>
      <ReconciliationFindingAlert currentRole={currentRole} />
      <HomeBand testId="home-band-orient" first hideChrome>
        {/* Glance strip — 6 KPI tiles summarising the portfolio at a glance.
            Shows "…" while locations/plan data is still loading. */}
        <div style={{ marginBottom: 16 }}>
          <KpiBand testId="home-glance-strip">
            <KpiTile
              testId="home-offices-kpi"
              label="Offices"
              tooltip={<HomeKpiPopover period="Today" source="Ripple portfolio">
                {glanceStats?.offices ?? "—"} operational + dormant offices across <strong style={{ color: T.tooltip.fg }}>{glanceStats?.activeMarkets ?? "—"} markets</strong>.
              </HomeKpiPopover>}
              tooltipStyle={HOME_KPI_TOOLTIP_STYLE}
              tooltipPlacement="viewport-clamped"
              value={glanceStats ? String(glanceStats.offices) : "…"}
              rawValue={glanceStats?.offices}
              formatter={(n) => n.toLocaleString()}
              animationKey={glanceStats != null}
              countFromZero
              sub={glanceStats
                ? canSeePlan ? `across ${glanceStats.activeMarkets} markets` : "in the portfolio"
                : "…"}
              color="var(--v2-accent)"
              tileIndex={0}
              height={KPI_TILE_ROW_HEIGHT}
            />
            <KpiTile
              testId="home-total-sqft-kpi"
              label="Total sq ft"
              tooltip={<HomeKpiPopover period="Today" source="Spacebase">
                Rentable area across operational + dormant offices. About <strong style={{ color: T.tooltip.fg }}>{glanceStats?.densityPerSeat == null ? "—" : glanceStats.densityPerSeat.toLocaleString()} sq ft per installed seat.</strong> Exited space excluded.
              </HomeKpiPopover>}
              tooltipStyle={HOME_KPI_TOOLTIP_STYLE}
              tooltipPlacement="viewport-clamped"
              value={glanceStats ? fmtGlanceSqft(glanceStats.totalSqft) : "…"}
              rawValue={glanceStats?.totalSqft}
              formatter={fmtGlanceSqft}
              animationKey={glanceStats != null}
              countFromZero
              sub={glanceStats
                ? `${glanceStats.densityPerSeat == null ? "—" : glanceStats.densityPerSeat.toLocaleString()} sq ft / seat`
                : "…"}
              color="var(--v2-green)"
              tileIndex={1}
              height={KPI_TILE_ROW_HEIGHT}
            />
            <KpiTile
              testId="home-total-seats-kpi"
              label="Total seats"
              value={glanceStats ? (glanceStats.totalSeats > 0 ? glanceStats.totalSeats.toLocaleString() : "—") : "…"}
              rawValue={glanceStats?.totalSeats > 0 ? glanceStats.totalSeats : undefined}
              formatter={(n) => n.toLocaleString()}
              animationKey={glanceStats != null}
              countFromZero
              sub="installed capacity"
              tooltip={<HomeKpiPopover period="Today" source="Envoy + admin">
                Desks installed across offices - the <strong style={{ color: T.tooltip.fg }}>denominator for seat utilization.</strong>
              </HomeKpiPopover>}
              tooltipStyle={HOME_KPI_TOOLTIP_STYLE}
              tooltipPlacement="viewport-clamped"
              color="var(--v2-gold)"
              tileIndex={2}
              height={KPI_TILE_ROW_HEIGHT}
            />
            <KpiTile
              testId="home-assigned-hc-kpi"
              label="Assigned HC"
              tooltip={<HomeKpiPopover
                period={glanceStats?.reportingMonth ?? "reporting period unavailable"}
                source="Workday"
              >
                People assigned to an office (employees + contractors). <strong style={{ color: T.tooltip.fg }}>Excludes {glanceStats?.distributedHeadcount?.toLocaleString() ?? "—"} distributed.</strong>
              </HomeKpiPopover>}
              tooltipStyle={HOME_KPI_TOOLTIP_STYLE}
              tooltipPlacement="viewport-clamped"
              value={glanceStats ? (glanceStats.assignedHc > 0 ? glanceStats.assignedHc.toLocaleString() : "—") : "…"}
              rawValue={glanceStats?.assignedHc > 0 ? glanceStats.assignedHc : undefined}
              formatter={(n) => n.toLocaleString()}
              animationKey={glanceStats != null}
              countFromZero
              sub="assigned to offices"
              color="var(--v2-teal)"
              tileIndex={3}
              height={KPI_TILE_ROW_HEIGHT}
            />
            {canSeeAnnualCost && <KpiTile
              testId="home-annual-cost-kpi"
              label="Annual Base Rent"
              tooltip={<HomeKpiPopover period="Today" source="Spacebase">
                {annualRent?.description ?? "Current-year contractual base rent for In-Use, Vacant-Held and Committed leases, net of eligible recorded sublease income, USD."}
                <br />{" "}<strong style={{ color: T.tooltip.fg }}>FX basis:</strong> {fxBasisSummary}
                {annualRent?.recoveryDetail && <div>{annualRent.recoveryDetail}</div>}
              </HomeKpiPopover>}
              tooltipStyle={HOME_KPI_TOOLTIP_STYLE}
              tooltipPlacement="viewport-clamped"
              value={annualRentLoaded ? fmtKpiUsd(annualRent?.totalRent) : "…"}
              rawValue={annualRentLoaded && annualRent?.totalRent != null && Number.isFinite(annualRent.totalRent) ? annualRent.totalRent : undefined}
              formatter={fmtKpiUsd}
              animationKey={annualRentLoaded}
              countFromZero
              sub={annualRent?.descriptor ?? "base rent, current year, USD"}
              color="var(--v2-purple)"
              tileIndex={4}
              height={KPI_TILE_ROW_HEIGHT}
            />}
            <KpiTile
              testId="home-attendance-kpi"
              label="Avg attendance"
              tooltip={<HomeKpiPopover
                period={(glanceStats?.attendanceMonth ?? "Reporting period unavailable").replace(/^As of\s+/i, "")}
                source="Envoy"
              >
                Share of assigned people who badged in, averaged over reporting workdays. <strong style={{ color: T.tooltip.fg }}>{glanceStats?.attendanceCoveragePeriod ?? ""}{glanceStats?.attendanceCoveragePercent ?? "—"} of days counted;</strong> larger offices weighted more. The people view.
              </HomeKpiPopover>}
              tooltipStyle={HOME_KPI_TOOLTIP_STYLE}
              tooltipPlacement="viewport-clamped"
              value={glanceStats ? (glanceStats.avgAttendance != null ? `${Math.round(glanceStats.avgAttendance)}%` : "—") : "…"}
              rawValue={glanceStats?.avgAttendance != null ? Math.round(glanceStats.avgAttendance) : undefined}
              formatter={(n) => `${n}%`}
              animationKey={glanceStats != null}
              countFromZero
              sub="of assigned people"
              color="var(--v2-light)"
              tileIndex={5}
              height={KPI_TILE_ROW_HEIGHT}
            />
          </KpiBand>
        </div>
      </HomeBand>

      {/* In focus is intentionally gated as one complete thematic band so the
          existing feature flag still removes the pane without leaving a label. */}
      {showInFocus && (
        <HomeBand testId="home-band-attention" hideChrome>
          {/* In focus — full width, portrait cards, decisions folded in. */}
          <div style={{ marginBottom: 16 }}>
            <InFocusSection
              onCardClick={handleCardClick}
              locationRisk={locationRisk}
              radarEnabled={canSeeRadar}
              marketsEnabled={canSeePlan}
              onNavigate={(urlString) => {
                const params = new URLSearchParams(urlString.replace(/^\?/, ""));
                const v = params.get("view");
                if (v === "infocus-radar" && canSeeRadar) {
                  navigate("/infocus-radar", {
                    state: { dismissedCards: locationRisk?.dismissedCards ?? [] },
                  });
                } else if (onNavigate) {
                  onNavigate(urlString);
                }
              }}
              isLoading={infocusLoading}
              inFocusLayout={inFocusLayout}
            />
          </div>
        </HomeBand>
      )}

      {canSeeCostCommitments && <HomeBand label="Cost & commitments" testId="home-band-cost-commitments">
        {/* Exposure/action summaries share a 60/40 row; exposure detail
            breaks out below both panes when expanded. */}
        <LeaseCommitmentsBand
          derivedLeases={derivedLeases}
          onNavigate={onNavigate}
          locationScopeData={homeAttendance}
        />
      </HomeBand>}

      <HomeBand label="Space & people" testId="home-band-space-people">
        <AttendancePane
          currentRole={currentRole}
          sharedAttendance={homeAttendance}
          sharedLoading={!attendanceLoaded}
          sharedError={attendanceError}
        />
        <SpaceHeadroomV2 markets={planMarkets} onNavigate={onNavigate} planEnabled={canSeePlan} />
      </HomeBand>

      {canSeeProjects && <HomeBand testId="home-band-in-motion" hideChrome>
        <PortfolioMotionPane
          model={portfolioMotionModel}
          loading={motionDataLoading || !planMarketsLoaded}
          projectsEnabled={canSeeProjects}
          onNavigate={navigate}
        />
      </HomeBand>}

      <HomeBand label="Operating discipline" testId="home-band-operating-discipline">
        <div
          data-testid="portfolio-command-layout"
          className="portfolio-command-layout"
          style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 16, marginBottom: 16, alignItems: "start" }}
        >
          <style>{`
            .portfolio-command-layout > * { min-width: 0; }
          `}</style>
          <PortfolioCommandTile
            onNavigate={onNavigate}
            markets={planMarkets}
            locations={locations}
            planEnabled={canSeePlan}
            reviewsEnabled={canSeeReviews}
          />
        </div>
      </HomeBand>

      {/* Card detail drawer */}
      <SlideSheet
        open={!!drawerCard}
        onClose={handleCloseDrawer}
        width={420}
        scrimTestId="v2-focus-drawer-scrim"
        panelTestId="v2-focus-drawer"
        hideCloseButton
      >
        {drawerCard && (
          <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>

            {/* ── Photo header (120px) — image when available, type-icon watermark fallback ── */}
            <div style={{ position: "relative", height: 120, flexShrink: 0, overflow: "hidden", background: "var(--v2-tint)" }}>
              {drawerCard.imageUrl ? (
                <img
                  src={drawerCard.imageUrl}
                  alt=""
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <div style={{ width: "100%", height: "100%", background: T.drawerPhotoGradient, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {(() => { const DrawerIcon = typeIcon(drawerCard.cat); return <DrawerIcon size={36} strokeWidth={1.4} style={{ color: T.whiteFade }} />; })()}
                </div>
              )}
              <DrawerCloseButton onClick={handleCloseDrawer} />
            </div>

            {/* ── Header band ── */}
            <div style={{
              background: TONE_BG[drawerCard.band] ?? TONE_BG.slate,
              padding: "14px 18px 12px",
              borderBottom: `1px solid ${TONE_BORDER[drawerCard.band] ?? "var(--v2-line)"}`,
              flexShrink: 0,
            }}>
              {/* Chips row — close button lives in the photo header above */}
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                <span style={{
                  fontSize: T.fsXs, fontWeight: T.fwSemibold, padding: "2px 10px", borderRadius: 6,
                  background: INFOCUS_TYPE_COLOR[drawerCard.cat] ?? INFOCUS_TYPE_COLOR.Watch,
                  color: T.white,
                  letterSpacing: ".04em", textTransform: "uppercase",
                }}>
                  {drawerCard.cat}
                </span>
                <span style={{
                  fontSize: T.fsXs, fontWeight: T.fwSemibold, padding: "2px 10px", borderRadius: 6,
                  background: "rgba(0,0,0,.07)", color: "var(--v2-mid)",
                }}>
                  Real Estate
                </span>
              </div>
              {/* Location name */}
              <div style={{ fontSize: T.fsLg, fontWeight: T.fwBold, color: "var(--v2-dark)", lineHeight: 1.25 }}>
                {[canSeePlan ? drawerCard.market : null, drawerCard.nm].filter(Boolean).join(" · ")}
              </div>
              {/* Sub-line: locationId · leaseType · Spacebase (provenance inline when lease present) */}
              {(drawerCard.locationId || drawerCard.leaseType || drawerCard.leaseId) && (
                <div style={{ fontSize: T.fsXs, color: "var(--v2-mid)", marginTop: 3 }}>
                  {[drawerCard.locationId, drawerCard.leaseType, drawerCard.leaseId ? "Spacebase" : null].filter(Boolean).join(" · ")}
                </div>
              )}
            </div>

            {/* ── Scrollable body ── */}
            <div style={{ flex: 1, overflow: "auto", padding: "18px 20px", paddingBottom: 28 }}>

              {/* Headline + why sentence */}
              <div style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: "var(--v2-dark)", marginBottom: 4 }}>
                {drawerCard.sm}
              </div>
              {(drawerCard.rankReason || drawerCard.issue) && (
                <div style={{ fontSize: T.fsBase, color: "var(--v2-mid)", marginBottom: 14, lineHeight: 1.5 }}>
                  {drawerCard.rankReason ?? String(drawerCard.issue ?? "").split(".")[0] + "."}
                </div>
              )}

              {/* ── Non-Insight content ── */}
              {drawerCard.cat !== "Insight" && (
                <>
                  {/* Facts grid (2-column; CD cards add a 5th "Notice due" cell) */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
                    <div style={{ background: "var(--v2-tint)", border: "1px solid var(--v2-line)", borderRadius: 10, padding: "10px 12px" }}>
                      <div style={{ fontSize: T.fsXs, color: T.mid, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 4 }}>Annual rent</div>
                      <div style={{ fontSize: T.fsLg, fontWeight: T.fwDisplay, color: "var(--v2-dark)" }}>
                        {formatDrawerRent(drawerCard.annualRentUsd)}
                      </div>
                    </div>
                    <div style={{ background: "var(--v2-tint)", border: "1px solid var(--v2-line)", borderRadius: 10, padding: "10px 12px" }}>
                      <div style={{ fontSize: T.fsXs, color: T.mid, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 4 }}>Expires</div>
                      <div style={{
                        fontSize: T.fsBase, fontWeight: T.fwBold,
                        color: drawerCard.daysUntilExpiry != null && drawerCard.daysUntilExpiry <= 90
                          ? "var(--v2-red)" : "var(--v2-dark)",
                      }}>
                        {formatDrawerExpiry(drawerCard.leaseExpiry, drawerCard.daysUntilExpiry)}
                      </div>
                    </div>
                    <div style={{ background: "var(--v2-tint)", border: "1px solid var(--v2-line)", borderRadius: 10, padding: "10px 12px" }}>
                      <div style={{ fontSize: T.fsXs, color: T.mid, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 4 }}>Outlook</div>
                      <div style={{ fontSize: T.fsLg, fontWeight: T.fwBold, color: TONE_FG[drawerCard.band] ?? "var(--v2-dark)" }}>
                        {drawerCard.outlook ?? "—"}
                      </div>
                    </div>
                    <div style={{ background: "var(--v2-tint)", border: "1px solid var(--v2-line)", borderRadius: 10, padding: "10px 12px" }}>
                      <div style={{ fontSize: T.fsXs, color: T.mid, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 4 }}>Space fit</div>
                      <div style={{ fontSize: T.fsLg, fontWeight: T.fwBold, color: "var(--v2-dark)" }}>
                        {drawerCard.spaceFitLabel ?? "—"}
                      </div>
                    </div>
                    {/* Notice due — only for CD cards where the countdown (daysUntilNotice)
                        targets the critical-date event rather than the lease end date.
                        Showing it separately keeps "Expires" paired with the lease end
                        date and avoids a countdown/date mismatch. */}
                    {drawerCard.noticeDueDate != null && (
                      <div style={{ background: "var(--v2-tint)", border: "1px solid var(--v2-line)", borderRadius: 10, padding: "10px 12px" }}>
                        <div style={{ fontSize: T.fsXs, color: T.mid, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 4 }}>Notice due</div>
                        <div style={{
                          fontSize: T.fsBase, fontWeight: T.fwBold,
                          color: drawerCard.daysUntilNotice != null && drawerCard.daysUntilNotice <= 90
                            ? "var(--v2-red)" : "var(--v2-dark)",
                        }}>
                          {formatDrawerExpiry(drawerCard.noticeDueDate, drawerCard.daysUntilNotice)}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Term-runway timeline */}
                  {drawerCard.leaseExpiry != null && drawerCard.daysUntilExpiry != null && (
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: T.fsXs, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: ".05em", color: T.mid, marginBottom: 8 }}>
                        Term runway
                      </div>
                      {(() => {
                        const totalDays = 365;
                        const remaining = Math.max(0, drawerCard.daysUntilExpiry);
                        const elapsed   = totalDays - Math.min(remaining, totalDays);
                        const pct       = Math.min(100, Math.round((elapsed / totalDays) * 100));
                        const trackColor = remaining <= 90 ? "var(--v2-red)" : remaining <= 180 ? "var(--v2-amber)" : "var(--v2-accent)";
                        return (
                          <>
                            <div style={{ position: "relative", height: 6, background: "var(--v2-hair)", borderRadius: 999, marginBottom: 6 }}>
                              <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${pct}%`, borderRadius: 999, background: trackColor }} />
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: T.fsXs, color: "var(--v2-mid)" }}>
                              <span>Today</span>
                              <span style={{ color: remaining <= 90 ? "var(--v2-red)" : "var(--v2-mid)", fontWeight: T.fwSemibold }}>
                                {formatDrawerExpiry(drawerCard.leaseExpiry, null)}
                              </span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}

                  {/* ── Three-tier action zone ── */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 6 }}>

                    {/* Tier 1 — Decision: section label + full-width filled primary button */}
                    {(drawerCard.cat === "Decision" || drawerCard.cat === "Action") && drawerCard.leaseId && canSeeLeases && (
                      <div>
                        <div style={{ fontSize: T.fsXs, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: ".05em", color: T.mid, marginBottom: 6 }}>
                          Decision
                        </div>
                        <button
                          onClick={() => { if (canSeeLeases) navigate(buildLeaseUrl(drawerCard.leaseId)); }}
                          data-testid="v2-focus-drawer-renewal-cta"
                          style={{
                            display: "block", textAlign: "center", width: "100%",
                            background: "var(--v2-accent)", color: T.white,
                            borderRadius: 10, padding: "10px 0", border: "none",
                            fontSize: T.fsBase, fontWeight: T.fwBold,
                            cursor: "pointer",
                          }}
                        >
                          Start a renewal decision →
                        </button>
                      </div>
                    )}

                    {/* Tier 2 — Acknowledge / Snooze pair (or snooze picker in-place) */}
                    {isEditor && drawerCard.cardKey && (
                      snoozePickerOpen ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ fontSize: T.fsXs, color: T.mid, whiteSpace: "nowrap" }}>Snooze for:</span>
                          {[7, 30].map(days => (
                            <button
                              key={days}
                              data-testid={`v2-focus-snooze-${days}d`}
                              onClick={() => handleSnooze(drawerCard, days)}
                              style={{ background: "var(--v2-accent)", border: "none", borderRadius: 8, padding: "5px 10px", fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.white, cursor: "pointer" }}
                            >
                              {`${days}d`}
                            </button>
                          ))}
                          <button
                            data-testid="v2-focus-snooze-cancel"
                            onClick={() => setSnoozePickerOpen(false)}
                            style={{ background: "none", border: "1px solid var(--v2-line)", borderRadius: 8, padding: "5px 8px", fontSize: T.fsXs, fontWeight: T.fwSemibold, color: "var(--v2-mid)", cursor: "pointer" }}
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                          <button
                            data-testid="v2-focus-acknowledge"
                            onClick={() => handleAcknowledge(drawerCard)}
                            style={{ background: "none", border: "1px solid var(--v2-line)", borderRadius: 8, padding: "10px 0", fontSize: T.fsBase, fontWeight: T.fwSemibold, color: "var(--v2-mid)", cursor: "pointer" }}
                          >
                            Dismiss
                          </button>
                          <button
                            data-testid="v2-focus-snooze"
                            onClick={() => setSnoozePickerOpen(true)}
                            style={{ background: "none", border: "1px solid var(--v2-line)", borderRadius: 8, padding: "10px 0", fontSize: T.fsBase, fontWeight: T.fwSemibold, color: "var(--v2-mid)", cursor: "pointer" }}
                          >
                            Snooze
                          </button>
                        </div>
                      )
                    )}

                    {/* LinkedWorkflowRunsIndicator — between Tier 2 and Tier 3 */}
                    {canSeeWorkflows && drawerCard.cat === "Decision" && drawerCard.locationId && (onOpenLinkedRun || (isEditor && onStartWorkflow)) && (
                      <div style={{ display: "flex", alignItems: "center", gap: 10, borderTop: "1px solid var(--v2-line)", paddingTop: 12 }}>
                        <LinkedWorkflowRunsIndicator
                          sourceType="decision"
                          sourceId={drawerCard.locationId}
                          onOpenRun={onOpenLinkedRun}
                        />
                        {isEditor && onStartWorkflow && (
                          <button
                            type="button"
                            data-testid="v2-decision-start-workflow"
                            onClick={() => onStartWorkflow({
                              type: "decision",
                              id: drawerCard.locationId,
                              label: drawerCard.nm ? `${drawerCard.nm} - ${drawerCard.sm || "decision"}` : "Decision",
                              locationId: drawerCard.locationId,
                            })}
                            style={{
                              background: "var(--v2-accent)", color: T.white, border: "none",
                              borderRadius: 8, padding: "6px 12px", fontSize: T.fsXs,
                              fontWeight: T.fwSemibold, cursor: "pointer",
                            }}
                          >
                            Start action
                          </button>
                        )}
                      </div>
                    )}

                    {/* Tier 3 — Quiet nav links row: "Model a scenario · Open lease → · Open location →" */}
                    {(() => {
                      const showModel = canSeePlan && (drawerCard.cat === "Decision" || drawerCard.cat === "Action") && !!drawerCard.leaseId;
                      const showOpenLease = canSeeLeases && !!drawerCard.leaseId;
                      const showOpenLocation = canSeeOffices && !!drawerCard.locationId;
                      if (!showModel && !showOpenLease && !showOpenLocation) return null;
                      const navLinkStyle = {
                        background: "none", border: "none", padding: 0,
                        fontSize: T.fsSm, color: "var(--v2-accent)",
                        fontWeight: T.fwSemibold, cursor: "pointer",
                      };
                      const links = [];
                      if (showModel) links.push(
                        <button key="model" data-testid="v2-focus-drawer-model-cta" onClick={() => { if (canSeePlan) navigate(buildMarketPlanUrl("whatif")); }} style={navLinkStyle}>
                          Model a scenario
                        </button>
                      );
                      if (showOpenLease) links.push(
                        <button key="lease" data-testid="v2-focus-drawer-open-lease" onClick={() => { if (canSeeLeases) navigate(buildLeaseUrl(drawerCard.leaseId)); }} style={navLinkStyle}>
                          Open lease →
                        </button>
                      );
                      if (showOpenLocation) links.push(
                        <button key="location" data-testid="v2-focus-drawer-open-location" onClick={() => { if (canSeeOffices) navigate(buildLocationUrl(drawerCard.locationId)); }} style={navLinkStyle}>
                          Open location →
                        </button>
                      );
                      return (
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          {links.map((link, i) => (
                            <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                              {i > 0 && <span style={{ color: "var(--v2-light)", fontSize: T.fsSm, userSelect: "none" }}>·</span>}
                              {link}
                            </span>
                          ))}
                        </div>
                      );
                    })()}

                  </div>
                </>
              )}

              {/* Insight CTA path (unchanged) */}
              {canSeeReports && drawerCard.cat === "Insight" && drawerCard.deepLink && (
                <div style={{ marginTop: 8 }}>
                  <a
                    href={drawerCard.deepLink}
                    data-testid="v2-insight-drawer-cta"
                    style={{
                      display: "inline-block",
                      background: "var(--v2-accent)", color: T.white,
                      borderRadius: 8, padding: "6px 12px",
                      fontSize: T.fsXs, fontWeight: T.fwSemibold,
                      textDecoration: "none",
                    }}
                  >
                    {drawerCard.action ?? "View report"} →
                  </a>
                </div>
              )}
            </div>

            {/* ── Footer (structural border only — actions moved into body tiers) ── */}
            <div style={{ borderTop: "1px solid var(--v2-line)", flexShrink: 0, height: 1 }} />

          </div>
        )}
      </SlideSheet>
    </div>
  );
}
